import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import AlleAnsatteWidget from './AlleAnsatteWidget';
import UnimicroOrdersWidget from './UnimicroOrdersWidget';
import SpaceInvaders from './SpaceInvaders';
import { fetchQuickLinks } from '../services/quickLinkService';
import { fetchWidgetConfigs, fetchWidgetCategories } from '../services/widgetConfigService';
import { fetchUserQuickActions, saveUserQuickActions } from '../services/userQuickActionService';
import { fetchDashboardStats, fetchAlertOrders, fetchMachineHours, fetchAbsencesToday, fetchProductionSummary } from '../services/dashboardService';
import { fetchPurchaseSummary } from '../services/steelPurchaseService';
import { fetchDailySales, fetchSellerSales } from '../services/salesProgressService';
import { fetchProductionResources, fetchProductionJobs } from '../services/productionPlanService';
import { fetchMyNotifications } from '../services/notificationService';
import { searchWorkOrders } from '../services/workOrderService';
import ChatPanel from './ChatPanel';
import TeamInfoPanel from './TeamInfoPanel';
import WeatherHeaderChip from './WeatherHeaderChip';
import RadialSpeedDial from './RadialSpeedDial';
import { getCatalogEntry, passesVisibilityLevel, sortCategoryNames } from '../config/widgetCatalog';
import { getServerUrl, getApiUrl } from '../utils/apiUrl';
import { getAllUsers } from '../services/userService';
import { fetchUserPermissionsById } from '../services/userPermissionsService';
import { fetchTeamsForUser } from '../services/teamService';
import {
  FiInbox, FiFileText, FiFile, FiRefreshCw, FiCalendar, FiList, FiTruck, FiClock,
  FiDollarSign, FiSearch, FiGrid, FiPackage, FiUsers, FiBarChart2, FiUserCheck,
  FiBookOpen, FiColumns, FiUserPlus, FiGlobe, FiClipboard, FiTool, FiCpu, FiAward,
  FiArchive, FiMapPin, FiBell, FiRss, FiMessageSquare, FiMail, FiCloud, FiHash,
  FiEdit3, FiLayers, FiZap, FiTrendingUp, FiPieChart, FiMonitor, FiSettings, FiLink,
  FiShield, FiTarget, FiLock, FiTerminal, FiTrello, FiDatabase, FiSliders, FiUser, FiBox, FiSave,
  FiHome, FiStar, FiLogOut, FiChevronLeft, FiChevronRight, FiChevronDown, FiAlertTriangle, FiAlertCircle, FiActivity, FiPlus, FiExternalLink, FiPhone, FiShare2,
  FiCheck, FiX, FiArrowUp, FiArrowDown, FiShoppingCart, FiEye,
} from 'react-icons/fi';
import {
  MdInbox, MdAssignment, MdListAlt, MdSyncAlt, MdCalendarMonth, MdBarChart,
  MdLocalShipping, MdInventory, MdTimeline, MdExtension, MdSearch, MdBuild,
  MdArchive as MdInboxArchive, MdGroups, MdTrendingUp as MdTrending, MdCalendarToday,
  MdDateRange, MdBusiness, MdPublic, MdEngineering, MdPhone, MdBeachAccess,
  MdPrecisionManufacturing, MdMonitor, MdNotificationsActive, MdVerified,
  MdWarehouse, MdPlace, MdShoppingCart, MdInventory2, MdNotifications,
  MdIosShare, MdNewspaper, MdSmartToy, MdEmail, MdWbSunny, MdScale, MdEdit,
  MdDesignServices, MdBolt, MdDashboard, MdAnalytics, MdAdminPanelSettings,
  MdSettings, MdLink, MdSupervisorAccount, MdHistory, MdStorage, MdBackup,
  MdWidgets, MdPerson, MdDescription, MdPhoneIphone, MdTune,
} from 'react-icons/md';
import FileShareQuickCard from './FileShareQuickCard';
import { isKpiCardVisible, secondaryStatVisibilityKey } from '../config/kpiCardCatalog';
import {
  SectionHeader, StatusBadge, ProgressBar, KpiCard, MachineCard, AppCard, AlertItem, QuickFilter,
  OrderRow, OrderListHeader, DonutChart, LeftNav, tone, utilizationTone,
} from './dashboard';
import './FormStyles.css';
import '../tailwind.css';
import '../styles/dashboardTheme.css';

// Operator widgets - for ikke-admins (med permission-baserte tillegg)
const operatorWidgets = ({ userPermissions, userTeams }) => [
  // Show "Forespørsler" first if operator has permission
  ...(userPermissions?.canCreateWorkOrder ? [{
    id: 'foresporsler',
    title: 'Forespørsler',
    description: 'Registrer og administrer kundeforespørsler.',
  }] : []),
  // Show "Nytt Arbeidsordre" if operator has permission
  ...(userPermissions?.canCreateWorkOrder ? [{
    id: 'workOrders',
    title: 'Nytt Arbeidsordre',
    description: 'Opprett og administrer arbeidsordre med team og lokasjoner.',
  }] : []),
  // Show "Daglig Rapport" if operator is team leader with canCreateWorkOrder
  ...(userPermissions?.canCreateWorkOrder && userPermissions?.isTeamLeader ? [{
    id: 'dailyReport',
    title: 'Daglig Rapport',
    description: 'Dagens ordrer, frister, fravær og bemanning.',
  }] : []),
  {
    id: 'workOrdersList',
    title: 'Arbeidsordre',
    description: 'Se oversikt over alle arbeidsordre.',
  },
  {
    id: 'ordersFlow',
    title: 'Ordreflyt',
    description: 'Grafisk visning av arbeidsordre gjennom team.',
  },
  {
    id: 'varslinger',
    title: 'Varslinger',
    description: 'Se varsler når jobber er klare for ditt team.',
  },
  {
    id: 'avvik',
    title: 'Avvik',
    description: 'Se og håndter del-rapporter og avvik fra team.',
  },
  {
    id: 'fileShare',
    title: 'Fildeling',
    description: 'Send filer til andre brukere med varsling og automatisk sletting.',
  },
  {
    id: 'feed',
    title: 'Internfeed',
    description: 'Del oppdateringer og nyheter med teamet.',
  },
  {
    id: 'aiChat',
    title: 'AI Assistent',
    description: 'Chat med AI for hjelp og veiledning.',
  },
  {
    id: 'weather',
    title: 'Vær',
    description: 'Værmelding fra yr.no for din lokasjon.',
  },
  {
    id: 'vektKalkulator',
    title: 'Vekt Kalkulator',
    description: 'Beregn teoretisk vekt for metall- og plastprofiler.',
  },
  {
    id: 'pdfRedact',
    title: 'PDF Sladding',
    description: 'Sladd tekst og bilder i PDF-filer.',
  },
  {
    id: 'productionPlan',
    title: 'Produksjonsplan',
    description: 'Kalenderbasert produksjonsplanlegging.',
  },
  {
    id: 'jobQueue',
    title: 'Jobbkø',
    description: 'Visuell jobbkø med drag-and-drop prioritering per maskin.',
  },
  {
    id: 'cncProgramTransfer',
    title: 'CNC Program Overføring',
    description: 'Send NC-programmer direkte til FANUC maskinminne via FOCAS2.',
  },
  {
    id: 'deliveryDisplay',
    title: 'Ordre Oppfølging',
    description: 'Oversikt over alle aktive ordrer med leveringsdatoer.',
  },
  {
    id: 'steelCertificates',
    title: 'Sertifikater',
    description: 'Last opp og håndter materialsertifikater.',
  },
  {
    id: 'steelInventory',
    title: 'Stållager',
    description: 'Materiallager for stålplater med sporbarhet.',
  },
  {
    id: 'storageLocations',
    title: 'Lagringsplass',
    description: 'Se lagerplasser og QR-koder.',
  },
  {
    id: 'steelPurchases',
    title: 'Stålinnkjøp',
    description: 'Registrer stålinnkjøp med kg og salgspris per kg.',
  },
  {
    id: 'machiningTools',
    title: 'Verktøyinventar',
    description: 'Skjæreverktøy og innlegg — beholdning, slitasje og innkjøp.',
  },
  {
    id: 'steelMeasure',
    title: 'Stålplate Måling',
    description: 'Ta bilde av stålplate, korriger perspektiv og mål i mm.',
  },
  {
    id: 'incomingDelivery',
    title: 'Mottatte Pakker',
    description: 'Registrer mottatte pakker og leveranser for arbeidsordre.',
  },
  // Show "Sporing Oversikt" if operator can create orders or is in Levering team
  ...(userPermissions?.canCreateWorkOrder || userTeams?.some(t => (t.name || '').toLowerCase().includes('levering')) ? [{
    id: 'deliveryTracking',
    title: 'Sporing Oversikt',
    description: 'Global oversikt over leveranser via Bring og DSV med sanntids status.',
  }] : []),
  {
    id: 'partsExplorer',
    title: 'Deler Utforsker',
    description: 'Søk etter deler og se hvilke ordrer de er brukt i.',
  },
  // Show "Team Medlemmer" if user is team leader
  ...(userPermissions?.isTeamLeader ? [{
    id: 'teamMembers',
    title: 'Team Medlemmer',
    description: 'Administrer team medlemskap.',
  }] : []),
  // Show "KPI Dashboard" if operator has canCreateWorkOrder permission
  ...(userPermissions?.canCreateWorkOrder ? [{
    id: 'kpiDashboard',
    title: 'KPI Dashboard',
    description: 'Sanntids produksjonsmetriks og ytelse.',
  }] : []),
  // Show "Teamkapasitet" if operator has canCreateWorkOrder permission
  ...(userPermissions?.canCreateWorkOrder ? [{
    id: 'teamCapacity',
    title: 'Teamkapasitet',
    description: 'Se tilgjengelige timer vs. solgte timer per team per uke.',
  }] : []),
  ...(userPermissions?.canCreateWorkOrder || userPermissions?.isTeamLeader ? [{
    id: 'smartScheduler',
    title: 'Smart Planlegging',
    description: 'AI-basert planlegging: analyser ordrer, kapasitet og leveringsfrister.',
  }] : []),
  {
    id: 'operatorWork',
    title: 'Min Arbeidsoversikt',
    description: 'Se hva du har jobbet med denne måneden basert på statusrapportering.',
  },
  // Show "Unimicro Oppslag" if operator has canCreateWorkOrder permission
  ...(userPermissions?.canCreateWorkOrder ? [{
    id: 'unimicrLookup',
    title: 'Unimicro Oppslag',
    description: 'Søk etter ordre og ordrelinjer fra Unimicro.',
  }] : []),
  // Show "Unimicro Ordrer" if user can create orders or is in Fakturering team
  ...(userPermissions?.canCreateWorkOrder || userTeams?.some(t => (t.name || '').toLowerCase().includes('fakturering')) ? [{
    id: 'unimicroOrders',
    title: 'Unimicro Ordrer',
    description: 'Oversikt over alle ordrer i Unimicro med filtre.',
  }] : []),
  // Show "Priskalkulator (BOM)" if operator has canCreateWorkOrder permission
  ...(userPermissions?.canCreateWorkOrder ? [{
    id: 'bom',
    title: 'Priskalkulator (BOM)',
    description: 'Kalkuler materialer, arbeid og margin for forespørsler før tilbud sendes.',
  }] : []),
  // Show "E-post" if operator has canCreateWorkOrder permission
  ...(userPermissions?.canCreateWorkOrder ? [{
    id: 'email',
    title: 'E-post',
    description: 'Send og motta e-post med kobling til ordrer og forespørsler.',
  }] : []),
  {
    id: 'alleAnsatte',
    title: 'Felles Kalender',
    description: 'Se produksjonsplanen for alle ansatte på tvers av team.',
  },
  {
    id: 'phoneDirectory',
    title: 'Telefonliste',
    description: 'Telefonnumre til alle ansatte.',
  },
  {
    id: 'absenceRequest',
    title: 'Fravær',
    description: 'Registrer ferie, sykdom eller annet fravær.',
  },
  {
    id: 'forbruksskap',
    title: 'Forbruksskap',
    description: 'Lagerbeholdning og forbruk av forbruksmateriell med QR-etiketter.',
  },
  {
    id: 'salesProgress',
    title: 'Salgsprogresjon',
    description: 'Daglig salgsprogresjon mot salgsmål fra Unimicro.',
  },
  // Show "Administrasjonspanel" if operator has canManageEmployees permission
  ...(userPermissions?.canManageEmployees ? [{
    id: 'adminPanel',
    title: 'Administrasjonspanel',
    description: 'Samlet administrasjon av brukere, roller, team, tilganger og organisasjonskart.',
  }] : []),
  // Show "Audit Logger" if operator has canManageEmployees permission
  ...(userPermissions?.canManageEmployees ? [{
    id: 'auditLog',
    title: 'Audit Logger',
    description: 'Komplett oversikt over alle handlinger — innlogging, ordrer, status, dokumenter, konfigurasjon m.m.',
  }] : []),
  {
    id: 'cadViewer',
    title: 'CAD Viewer',
    description: 'Vis og utforsk DWG/DXF tegninger direkte i nettleseren.',
  },
  {
    id: 'adamCad',
    title: 'Adam CAD Generator',
    description: 'AI-genererte 3D-modeller fra tekstbeskrivelser via Adam.',
  },
  {
    id: 'machineMonitor',
    title: 'Maskin Monitor',
    description: 'Sanntids maskinovervåking via FOCAS2.',
  },
  {
    id: 'alarmLog',
    title: 'Alarmlogg',
    description: 'Se og filtrer maskin-alarmer med detaljert informasjon.',
  },
];

// Full widget list for admins and users with permissions
const allWidgets = ({ user, companyName, userPermissions, userTeams }) => ([
  {
    id: 'adminPanel',
    title: 'Administrasjonspanel',
    description: 'Samlet administrasjon av brukere, roller, team, tilganger og organisasjonskart.',
  },
  {
    id: 'foresporsler',
    title: 'Forespørsler',
    description: 'Registrer og administrer kundeforespørsler.',
  },
  {
    id: 'configuration',
    title: 'Konfigurasjon',
    description: 'Åpne kontrollpanelet for innstillinger.',
  },
  // Only show "Nytt Arbeidsordre" if user has permission
  ...(userPermissions?.canCreateWorkOrder ? [{
    id: 'workOrders',
    title: 'Nytt Arbeidsordre',
    description: 'Opprett og administrer arbeidsordre med team og lokasjoner.',
  }] : []),
  {
    id: 'workOrdersList',
    title: 'Arbeidsordre',
    description: 'Se oversikt over alle arbeidsordre.',
  },
  {
    id: 'ordersFlow',
    title: 'Ordreflyt',
    description: 'Grafisk visning av arbeidsordre gjennom team.',
  },
  {
    id: 'productionPlan',
    title: 'Produksjonsplan',
    description: 'Kalenderbasert produksjonsplanlegging.',
  },
  {
    id: 'jobQueue',
    title: 'Jobbkø',
    description: 'Visuell jobbkø med drag-and-drop prioritering per maskin.',
  },
  ...(user?.isAdmin || userPermissions?.canCreateWorkOrder ? [{
    id: 'deliveryDisplay',
    title: 'Ordre Oppfølging',
    description: 'Oversikt over alle aktive ordrer med leveringsdatoer.',
  }] : []),
  ...(user?.isAdmin || userPermissions?.canCreateWorkOrder || userTeams?.some(t => (t.name || '').toLowerCase().includes('levering')) ? [{
    id: 'deliveryTracking',
    title: 'Sporing Oversikt',
    description: 'Global oversikt over leveranser via Bring og DSV med sanntids status.',
  }] : []),
  {
    id: 'timeline',
    title: 'Tidslinje',
    description: 'Visuell oversikt over arbeidsordre i tid.',
  },
  {
    id: 'varslinger',
    title: 'Varslinger',
    description: 'Se varsler når jobber er klare for ditt team.',
  },
  {
    id: 'avvik',
    title: 'Avvik',
    description: 'Se og håndter del-rapporter og avvik fra team.',
  },
  {
    id: 'fileShare',
    title: 'Fildeling',
    description: 'Send filer til andre brukere med varsling og automatisk sletting.',
  },
  {
    id: 'weather',
    title: 'Vær',
    description: 'Værmelding fra yr.no for din lokasjon.',
  },
  {
    id: 'vektKalkulator',
    title: 'Vekt Kalkulator',
    description: 'Beregn teoretisk vekt for metall- og plastprofiler.',
  },
  {
    id: 'pdfRedact',
    title: 'PDF Sladding',
    description: 'Sladd tekst og bilder i PDF-filer.',
  },
  {
    id: 'quickLinks',
    title: 'Administrer Hurtiglenker',
    description: 'Legg til, rediger og slett lenker som vises på dashboardet.',
  },
  {
    id: 'widgetManager',
    title: 'Modul-oppsett',
    description: 'Kategoriser, sorter og styr synligheten til modulene i hovedmenyen.',
  },
  {
    id: 'kpiDashboard',
    title: 'KPI Dashboard',
    description: 'Sanntids produksjonsmetriks og ytelse.',
  },
  {
    id: 'salesProgress',
    title: 'Salgsprogresjon',
    description: 'Daglig salgsprogresjon mot salgsmål fra Unimicro.',
  },
  {
    id: 'roles',
    title: 'Roller',
    description: 'Administrer selskapets roller.',
  },
  {
    id: 'employeeCalendar',
    title: 'Ansattplanlegger',
    description: 'Planlegg ferie og sykefravær for teamet.',
  },
  {
    id: 'anstattkalendar',
    title: 'Anstattkalendar',
    description: 'Vis alle oppføringer i en visuell tidsplan.',
  },
  {
    id: 'machines',
    title: 'Maskiner',
    description: 'Vedlikehold maskiner og tilordninger.',
  },
  {
    id: 'machineMonitor',
    title: 'Maskin Monitor',
    description: 'Sanntids maskinovervåking via FOCAS2.',
  },
  {
    id: 'alarmLog',
    title: 'Alarmlogg',
    description: 'Se og filtrer maskin-alarmer med detaljert informasjon.',
  },
  // CNC Program Transfer - visible to all, machines filtered by operator assignment
  {
    id: 'cncProgramTransfer',
    title: 'CNC Program Overføring',
    description: 'Send NC-programmer direkte til FANUC maskinminne via FOCAS2.',
  },
  {
    id: 'teams',
    title: 'Team',
    description: 'Sett sammen team ved å tilordne ansatte til maskiner.',
  },
  {
    id: 'teamMembers',
    title: 'Team Medlemmer',
    description: 'Administrer team medlemskap (kun for teamledere).',
  },
  {
    id: 'steelCertificates',
    title: 'Sertifikater',
    description: 'Last opp og håndter materialsertifikater.',
  },
  {
    id: 'steelInventory',
    title: 'Stållager',
    description: 'Materiallager for stålplater med sporbarhet.',
  },
  {
    id: 'steelPurchases',
    title: 'Stålinnkjøp',
    description: 'Registrer stålinnkjøp med kg og salgspris per kg.',
  },
  {
    id: 'machiningTools',
    title: 'Verktøyinventar',
    description: 'Skjæreverktøy og innlegg — beholdning, slitasje og innkjøp.',
  },
  {
    id: 'steelMeasure',
    title: 'Stålplate Måling',
    description: 'Ta bilde av stålplate, korriger perspektiv og mål i mm.',
  },
  {
    id: 'auditLog',
    title: 'Audit Logger',
    description: 'Komplett oversikt over alle handlinger — innlogging, ordrer, status, dokumenter, konfigurasjon m.m.',
  },
  {
    id: 'systemBackup',
    title: 'Systembackup',
    description: 'Ta backup og gjenopprett hele systemet.',
  },
  {
    id: 'brukere',
    title: 'Brukere',
    description: 'Administrer brukere, passord og tilgang.',
  },
  {
    id: 'teamCapacity',
    title: 'Teamkapasitet',
    description: 'Se tilgjengelige timer vs. solgte timer per team per uke.',
  },
  {
    id: 'smartScheduler',
    title: 'Smart Planlegging',
    description: 'AI-basert planlegging: analyser ordrer, kapasitet og leveringsfrister automatisk.',
  },
  {
    id: 'operatorWork',
    title: 'Operatør Arbeidsoversikt',
    description: 'Månedlig estimat av hva operatører har jobbet med basert på statusrapportering.',
  },
  {
    id: 'bom',
    title: 'Priskalkulator (BOM)',
    description: 'Kalkuler materialer, arbeid og margin for forespørsler før tilbud sendes.',
  },
  {
    id: 'statistics',
    title: 'Statistikk',
    description: 'Se oversikt over ordre og avdelingsytelse.',
  },
  {
    id: 'storageLocations',
    title: 'Lagringsplass',
    description: 'Administrer lagerplasser og QR-koder.',
  },
  {
    id: 'incomingDelivery',
    title: 'Mottatte Pakker',
    description: 'Registrer mottatte pakker og leveranser for arbeidsordre.',
  },
  {
    id: 'partsExplorer',
    title: 'Deler Utforsker',
    description: 'Søk etter deler og se hvilke ordrer de er brukt i.',
  },
  // Unimicro widgets - for admins and users who can create orders
  ...(user?.isAdmin || userPermissions?.canCreateWorkOrder ? [{
    id: 'unimicrLookup',
    title: 'Unimicro Oppslag',
    description: 'Søk etter ordre og ordrelinjer fra Unimicro.',
  }] : []),
  // Unimicro Orders overview - admin, canCreateWorkOrder, or Fakturering team
  ...(user?.isAdmin || userPermissions?.canCreateWorkOrder || userTeams?.some(t => (t.name || '').toLowerCase().includes('fakturering')) ? [{
    id: 'unimicroOrders',
    title: 'Unimicro Ordrer',
    description: 'Oversikt over alle ordrer i Unimicro med filtre.',
  }] : []),
  // Only show Unimicro admin to admins
  ...(user?.isAdmin ? [{
    id: 'unimicro',
    title: 'Unimicro Admin',
    description: 'Database explorer og konfigurasjon.',
  }] : []),
  {
    id: 'email',
    title: 'E-post',
    description: 'Send og motta e-post med kobling til ordrer og forespørsler.',
  },
  {
    id: 'feed',
    title: 'Internfeed',
    description: 'Del oppdateringer og nyheter med teamet.',
  },
  {
    id: 'aiChat',
    title: 'AI Assistent',
    description: 'Chat med AI for hjelp og veiledning.',
  },
  // Daglig Rapport - visible to team leaders with canCreateWorkOrder
  ...(userPermissions?.canCreateWorkOrder && userPermissions?.isTeamLeader ? [{
    id: 'dailyReport',
    title: 'Daglig Rapport',
    description: 'Dagens ordrer, frister, fravær og bemanning.',
  }] : []),
  {
    id: 'alleAnsatte',
    title: 'Felles Kalender',
    description: 'Se produksjonsplanen for alle ansatte på tvers av team.',
  },
  {
    id: 'phoneDirectory',
    title: 'Telefonliste',
    description: 'Telefonnumre til alle ansatte.',
  },
  {
    id: 'absenceRequest',
    title: 'Fravær',
    description: 'Registrer ferie, sykdom eller annet fravær.',
  },
  {
    id: 'cadViewer',
    title: 'CAD Viewer',
    description: 'Vis og utforsk DWG/DXF tegninger direkte i nettleseren.',
  },
  {
    id: 'adamCad',
    title: 'Adam CAD Generator',
    description: 'AI-genererte 3D-modeller fra tekstbeskrivelser via Adam.',
  },
  {
    id: 'forbruksskap',
    title: 'Forbruksskap',
    description: 'Lagerbeholdning og forbruk av forbruksmateriell med QR-etiketter.',
  },
]);

// Guest widgets - for brukere som venter på godkjenning
const guestWidgets = () => [];

const widgetDefinitions = ({ user, companyName, userPermissions, userTeams }) => {
  // If user has no employee record and no permissions, they are a "guest" waiting for approval
  if (user && !user.isAdmin && !userPermissions?.employeeId) {
    return guestWidgets();
  }
  
  // If user is NOT admin, show operator widgets (with permission-based additions)
  if (user && !user.isAdmin) {
    return operatorWidgets({ userPermissions, userTeams });
  }
  
  // If user is admin, show all widgets (with permission checks)
  return allWidgets({ user, companyName, userPermissions, userTeams });
};

const MainForm = ({ user, token, onWidgetSelect, onCloseWidget, selectedWidget, companyName, logoUrl, dashboardBgUrl, headerBgUrl, rightPanelBgUrl, kpiCardBgColor, kpiCardVisibility, widgetButtonBgColor, headerTextColor, kpiCardTextColor, widgetMenuTextColor, rightPanelTextColor, greetingTextColor, unreadNotificationsCount = 0, assignedInquiriesCount = 0, userPermissions, userTeams = [], onSignOut,
  onlineUsers = [], showOnlineUsers, setShowOnlineUsers, showChat, setShowChat, unreadChatCount = 0, setUnreadChatCount, dmUser, setDmUser, socketRef, onlineUsersTimeoutRef }) => {
  // ===== Admin "view as user" preview state =====
  const [previewData, setPreviewData] = useState(null); // { user, permissions, teams }
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [usersList, setUsersList] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [showUserSelector, setShowUserSelector] = useState(false);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const userSelectorRef = useRef(null);

  // Effective values used for widget rendering (real or previewed)
  const isPreviewing = !!previewData;
  const effectiveUser = isPreviewing ? previewData.user : user;
  const effectivePermissions = isPreviewing ? previewData.permissions : userPermissions;
  const effectiveTeams = isPreviewing ? previewData.teams : userTeams;

  const widgets = widgetDefinitions({ user: effectiveUser, companyName, userPermissions: effectivePermissions, userTeams: effectiveTeams });
  const [quickLinks, setQuickLinks] = useState([]);
  const [widgetConfigs, setWidgetConfigs] = useState([]);
  const [categoryOrders, setCategoryOrders] = useState([]);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [expandedNavCategory, setExpandedNavCategory] = useState(null);
  const [rightPanelCollapsed, setRightPanelCollapsed] = useState(false);
  const [showMineMaskiner, setShowMineMaskiner] = useState(false);
  const [showMittTeam, setShowMittTeam] = useState(false);
  const [showRadialDial, setShowRadialDial] = useState(false);
  const [showSpaceInvaders, setShowSpaceInvaders] = useState(false);
  const [savedQuickActions, setSavedQuickActions] = useState(null);
  const [editingQuickActions, setEditingQuickActions] = useState(false);
  const [editingActionIds, setEditingActionIds] = useState([]);
  const [savingActions, setSavingActions] = useState(false);
  const [stats, setStats] = useState(null);
  const [expandedAlert, setExpandedAlert] = useState(null); // 'stopped' | 'overdue' | null
  const [alertOrders, setAlertOrders] = useState([]);
  const [alertLoading, setAlertLoading] = useState(false);
  const [expandedStat, setExpandedStat] = useState(null); // 'active' | 'production' | 'deadlines' | 'notifications' | null
  const [statItems, setStatItems] = useState([]);
  const [statLoading, setStatLoading] = useState(false);
  const [pendingFollowupOrders, setPendingFollowupOrders] = useState([]);
  const [registeredOrders, setRegisteredOrders] = useState([]);
  const [statTab, setStatTab] = useState('pending'); // 'pending' | 'registered'
  const statsGridRef = useRef(null);
  const [machineHours, setMachineHours] = useState([]);
  const [myMachineJobs, setMyMachineJobs] = useState([]);
  const [rotationLeaders, setRotationLeaders] = useState([]);
  const [advancingRotation, setAdvancingRotation] = useState(null);
  const [absencesToday, setAbsencesToday] = useState([]);
  const [absenceDate, setAbsenceDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [steelSummary, setSteelSummary] = useState(null);
  const [salesKpiData, setSalesKpiData] = useState(null);
  const [salesSellerData, setSalesSellerData] = useState([]);
  const [capacitySummary, setCapacitySummary] = useState(null);
  const [capacityWeeks, setCapacityWeeks] = useState(4);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const refreshTimer = useRef(null);
  const [showFellesKalender, setShowFellesKalender] = useState(false);
  const [showUnimicroOrders, setShowUnimicroOrders] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchOrders, setSearchOrders] = useState([]);
  const [searchOrdersLoading, setSearchOrdersLoading] = useState(false);
  const searchDebounceRef = useRef(null);
  const searchBoxRef = useRef(null);

  // ===== Dashboard redesign state =====
  const [navCollapsed, setNavCollapsed] = useState(() => {
    try { return localStorage.getItem('dsh_nav_collapsed') === '1'; } catch { return false; }
  });
  const [productionSummary, setProductionSummary] = useState(null);
  const [recentNotifications, setRecentNotifications] = useState([]);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef(null);
  const mainRef = useRef(null);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    try { localStorage.setItem('dsh_nav_collapsed', navCollapsed ? '1' : '0'); } catch { /* ignore */ }
  }, [navCollapsed]);

  // Live clock in the greeting (minute resolution)
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(t);
  }, []);

  // Auto-collapse the left nav on medium screens
  useEffect(() => {
    const onResize = () => { if (window.innerWidth < 1100) setNavCollapsed(true); };
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!showUserMenu) return;
    const handler = (e) => { if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setShowUserMenu(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showUserMenu]);

  const loadProductionSummary = useCallback(() => {
    if (!token) return;
    fetchProductionSummary(token).then(setProductionSummary).catch(() => {});
    fetchMyNotifications(token, true).then((n) => setRecentNotifications(Array.isArray(n) ? n.slice(0, 6) : [])).catch(() => {});
  }, [token]);

  useEffect(() => { loadProductionSummary(); }, [loadProductionSummary]);

  useEffect(() => {
    if (token) {
      fetchQuickLinks(token).then(setQuickLinks).catch(() => {});
      fetchWidgetConfigs(token).then(setWidgetConfigs).catch(() => {});
      fetchWidgetCategories(token).then(setCategoryOrders).catch(() => {});
      fetchUserQuickActions(token).then(actions => setSavedQuickActions(actions.map(a => a.widgetId))).catch(() => setSavedQuickActions([]));
      fetchDashboardStats(token).then(setStats).catch(() => {});
      fetchPurchaseSummary(token).then(setSteelSummary).catch(() => {});
      // Fetch sales KPI data (SALG team leader, Fakturering members, admin)
      const isSalgTeamLeader = (userPermissions?.teamLeaderTeamNames || []).some(n => n.toUpperCase().includes('SALG'));
      const isFaktureringMember = (userTeams || []).some(t => t.name?.toUpperCase().includes('FAKTURERING'));
      if (user?.isAdmin || isSalgTeamLeader || isFaktureringMember) {
        fetchDailySales(token, 30).then(setSalesKpiData).catch(() => {});
        fetchSellerSales(token).then(setSalesSellerData).catch(() => {});
      }
      if (user?.isAdmin || userPermissions?.canCreateWorkOrder) {
        fetchMachineHours(token).then(setMachineHours).catch(() => {});
      }
      // Fetch rotating team leaders (visible to all)
      fetch(`${getApiUrl()}/team-leader-rotation/current`, {
        headers: { Authorization: `Bearer ${token}` },
      }).then(r => r.ok ? r.json() : []).then(setRotationLeaders).catch(() => {});
      // Fetch user's assigned machines + their jobs for KPI card
      Promise.all([
        fetchProductionResources(token).catch(() => []),
        fetchProductionJobs(token).catch(() => []),
      ]).then(([resources, jobs]) => {
        const machines = (resources || []).filter(r => r.resourceType === 'machine');
        const activeJobs = (jobs || []).filter(j => !j.completed);
        const result = machines.map(m => {
          const mJobs = activeJobs
            .filter(j => j.resource_id === m.id || j.machine_id === m.machineId)
            .sort((a, b) => {
              if (a.start_time && b.start_time) return new Date(a.start_time) - new Date(b.start_time);
              if (a.start_time) return -1;
              if (b.start_time) return 1;
              return (a.id || 0) - (b.id || 0);
            });
          return {
            id: m.id,
            name: m.title,
            teamName: m.teamName,
            current: mJobs[0] || null,
            next: mJobs[1] || null,
            totalActive: mJobs.length,
          };
        }).filter(m => m.current || m.next);
        setMyMachineJobs(result);
      }).catch(() => {});
    }
  }, [token, user?.isAdmin, userPermissions?.canCreateWorkOrder]);

  // Load team capacity summary for KPI card (respects capacityWeeks)
  const loadCapacitySummary = useCallback(async () => {
    if (!token || !(user?.isAdmin || userPermissions?.canCreateWorkOrder)) return;
    const tcMon = new Date();
    const tcDay = tcMon.getDay();
    const tcDiff = tcMon.getDate() - tcDay + (tcDay === 0 ? -6 : 1);
    tcMon.setDate(tcDiff);
    tcMon.setHours(0, 0, 0, 0);
    const tcEnd = new Date(tcMon);
    tcEnd.setDate(tcEnd.getDate() + (capacityWeeks * 7) - 1);
    const tcStartStr = tcMon.toISOString().split('T')[0];
    const tcEndStr = tcEnd.toISOString().split('T')[0];
    try {
      const res = await fetch(`${getApiUrl()}/team-capacity?startDate=${tcStartStr}&endDate=${tcEndStr}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const tcData = res.ok ? await res.json() : null;
      if (!tcData) return setCapacitySummary(null);

      // Fetch widget settings for grouping
      let groupMode = false;
      let groupAssignments = {};
      let hiddenTeams = {};
      try {
        const settingsRes = await fetch(`${getApiUrl()}/team-capacity/widget-settings`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (settingsRes.ok) {
          const settings = await settingsRes.json();
          groupMode = settings.groupMode || false;
          groupAssignments = settings.groupAssignments || {};
          hiddenTeams = settings.hiddenTeams || {};
        }
      } catch (e) { /* ignore */ }

      const { teams, members, machines, absences, soldHours, config } = tcData;
      const membersByTeam = {};
      for (const m of members) {
        if (!membersByTeam[m.team_id]) membersByTeam[m.team_id] = [];
        membersByTeam[m.team_id].push(m);
      }
      const machinesByTeam = {};
      for (const m of (machines || [])) {
        if (!machinesByTeam[m.team_id]) machinesByTeam[m.team_id] = [];
        machinesByTeam[m.team_id].push(m);
      }
      const soldByTeam = {};
      for (const s of soldHours) {
        if (!soldByTeam[s.team_id]) soldByTeam[s.team_id] = 0;
        soldByTeam[s.team_id] += parseFloat(s.estimated_hours || 0);
      }
      const configByTeam = {};
      for (const c of (config || [])) {
        if (!configByTeam[c.team_id]) configByTeam[c.team_id] = new Set();
        if (c.resource_id !== 0) configByTeam[c.team_id].add(`${c.resource_type}_${c.resource_id}`);
      }
      const teamsWithConfig = new Set((config || []).map(c => c.team_id));

      const WORK_HOURS = 7.67;
      const teamCapacity = [];

      for (const team of teams) {
        const allMembers = membersByTeam[team.id] || [];
        const allMachines = machinesByTeam[team.id] || [];
        const hasConfig = teamsWithConfig.has(team.id);
        const safeConfig = configByTeam[team.id] || new Set();
        const teamMembers = hasConfig ? allMembers.filter(m => safeConfig.has(`employee_${m.employee_id}`)) : allMembers;
        const teamMachines = hasConfig ? allMachines.filter(m => safeConfig.has(`machine_${m.id}`)) : allMachines;
        const resourceCount = teamMembers.length + teamMachines.length;
        if (resourceCount === 0) continue;

        let teamAvailable = 0;
        for (let w = 0; w < capacityWeeks; w++) {
          const weekMon = new Date(tcMon);
          weekMon.setDate(weekMon.getDate() + (w * 7));
          const weekDays = [];
          for (let d = 0; d < 5; d++) {
            const wd = new Date(weekMon);
            wd.setDate(wd.getDate() + d);
            weekDays.push(wd.toISOString().split('T')[0]);
          }
          let totalAbsenceDays = 0;
          for (const member of teamMembers) {
            for (const abs of absences) {
              if (abs.employee_id === member.employee_id) {
                for (const ds of weekDays) {
                  if (ds >= abs.start_date && ds <= abs.end_date) totalAbsenceDays++;
                }
              }
            }
          }
          const totalWorkdays = resourceCount * 5;
          const availableWorkdays = totalWorkdays - totalAbsenceDays;
          teamAvailable += availableWorkdays * WORK_HOURS;
        }

        const teamSold = Math.round((soldByTeam[team.id] || 0) * 10) / 10;
        teamAvailable = Math.round(teamAvailable * 10) / 10;
        teamCapacity.push({
          teamId: team.id,
          teamName: team.name,
          memberCount: teamMembers.length,
          machineCount: teamMachines.length,
          available: teamAvailable,
          sold: teamSold,
          hasConfig,
        });
      }

      const groupColors = ['', '#8b5cf6', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#06b6d4', '#84cc16'];
      let displayItems = [];

      if (groupMode) {
        const groups = {};
        const ungrouped = [];
        for (const tc of teamCapacity) {
          const grp = groupAssignments[tc.teamId];
          if (grp && grp > 0) {
            if (!groups[grp]) groups[grp] = [];
            groups[grp].push(tc);
          } else {
            ungrouped.push(tc);
          }
        }
        for (const [grpNum, items] of Object.entries(groups).sort((a, b) => a[0] - b[0])) {
          if (items.length === 1) {
            displayItems.push({ ...items[0], groupNum: parseInt(grpNum) });
          } else {
            const combinedName = items.map(t => t.teamName).join(' + ');
            const combinedAvailable = Math.round(items.reduce((s, t) => s + t.available, 0) * 10) / 10;
            const combinedSold = Math.round(items.reduce((s, t) => s + t.sold, 0) * 10) / 10;
            const combinedMembers = items.reduce((s, t) => s + t.memberCount, 0);
            const combinedMachines = items.reduce((s, t) => s + t.machineCount, 0);
            displayItems.push({
              teamId: `group_${grpNum}`,
              teamName: combinedName,
              memberCount: combinedMembers,
              machineCount: combinedMachines,
              available: combinedAvailable,
              sold: combinedSold,
              hasConfig: items.some(t => t.hasConfig),
              groupNum: parseInt(grpNum),
            });
          }
        }
        for (const tc of ungrouped) {
          displayItems.push({ ...tc, groupNum: null });
        }
      } else {
        displayItems = teamCapacity.map(tc => ({ ...tc, groupNum: null }));
      }

      displayItems = displayItems.filter(tc => {
        if (typeof tc.teamId === 'string' && tc.teamId.startsWith('group_')) {
          return true;
        }
        return !hiddenTeams[tc.teamId];
      });

      let totalAvailable = Math.round(displayItems.reduce((s, t) => s + t.available, 0) * 10) / 10;
      let totalSold = Math.round(displayItems.reduce((s, t) => s + t.sold, 0) * 10) / 10;
      const utilization = totalAvailable > 0 ? Math.round((totalSold / totalAvailable) * 100) : 0;
      setCapacitySummary({ totalAvailable, totalSold, utilization, teamCount: displayItems.length, displayItems, groupColors, weeks: capacityWeeks });
    } catch (err) {
      console.error('[CAPACITY-KPI] Error:', err);
    }
  }, [token, capacityWeeks, user?.isAdmin, userPermissions?.canCreateWorkOrder]);

  useEffect(() => { loadCapacitySummary(); }, [loadCapacitySummary]);

  useEffect(() => {
    if (token) {
      fetchAbsencesToday(token, absenceDate).then(setAbsencesToday).catch(() => {});
    }
  }, [token, absenceDate]);

  // Fetch sales KPI data for users granted access via Modul innstillinger (when widgetConfigs load)
  useEffect(() => {
    if (!token || !widgetConfigs.length) return;
    const isSalgTL = (userPermissions?.teamLeaderTeamNames || []).some(n => n.toUpperCase().includes('SALG'));
    const isFakt = (userTeams || []).some(t => t.name?.toUpperCase().includes('FAKTURERING'));
    if (user?.isAdmin || isSalgTL || isFakt) return; // already fetched in main useEffect
    const salesCfg = widgetConfigs.find(c => c.widgetId === 'salesProgress');
    const passes = salesCfg
      ? passesVisibilityLevel(
          salesCfg.visibilityLevel || 'all',
          { user, userPermissions, allowedUserIds: salesCfg.allowedUserIds || [] }
        )
      : false;
    if (passes) {
      fetchDailySales(token, 30).then(setSalesKpiData).catch(() => {});
      fetchSellerSales(token).then(setSalesSellerData).catch(() => {});
    }
  }, [token, widgetConfigs, user, userPermissions, userTeams]);

  // Socket.IO: real-time dashboard refresh
  useEffect(() => {
    if (!socketRef || !token) return;
    const refreshDashboard = () => {
      if (refreshTimer.current) clearTimeout(refreshTimer.current);
      refreshTimer.current = setTimeout(() => {
        fetchDashboardStats(token).then(setStats).catch(() => {});
        loadProductionSummary();
        fetchPurchaseSummary(token).then(setSteelSummary).catch(() => {});
        // Refresh sales KPI
        const _isSalgTL = (userPermissions?.teamLeaderTeamNames || []).some(n => n.toUpperCase().includes('SALG'));
        const _isFakt = (userTeams || []).some(t => t.name?.toUpperCase().includes('FAKTURERING'));
        if (user?.isAdmin || _isSalgTL || _isFakt) {
          fetchDailySales(token, 30).then(setSalesKpiData).catch(() => {});
          fetchSellerSales(token).then(setSalesSellerData).catch(() => {});
        }
        fetchAbsencesToday(token, absenceDate).then(setAbsencesToday).catch(() => {});
        if (user?.isAdmin || userPermissions?.canCreateWorkOrder) {
          fetchMachineHours(token).then(setMachineHours).catch(() => {});
        }
        // Refresh rotating team leaders
        fetch(`${getApiUrl()}/team-leader-rotation/current`, {
          headers: { Authorization: `Bearer ${token}` },
        }).then(r => r.ok ? r.json() : []).then(setRotationLeaders).catch(() => {});
        // Refresh machine jobs KPI
        Promise.all([
          fetchProductionResources(token).catch(() => []),
          fetchProductionJobs(token).catch(() => []),
        ]).then(([resources, jobs]) => {
          const machines = (resources || []).filter(r => r.resourceType === 'machine');
          const activeJobs = (jobs || []).filter(j => !j.completed);
          const result = machines.map(m => {
            const mJobs = activeJobs
              .filter(j => j.resource_id === m.id || j.machine_id === m.machineId)
              .sort((a, b) => {
                if (a.start_time && b.start_time) return new Date(a.start_time) - new Date(b.start_time);
                if (a.start_time) return -1;
                if (b.start_time) return 1;
                return (a.id || 0) - (b.id || 0);
              });
            return { id: m.id, name: m.title, teamName: m.teamName, current: mJobs[0] || null, next: mJobs[1] || null, totalActive: mJobs.length };
          }).filter(m => m.current || m.next);
          setMyMachineJobs(result);
        }).catch(() => {});
        setRefreshTrigger(prev => prev + 1);
      }, 500);
    };
    socketRef.on('workOrderCreated', refreshDashboard);
    socketRef.on('workOrderUpdated', refreshDashboard);
    socketRef.on('workOrderDeleted', refreshDashboard);
    socketRef.on('productionJobsUpdated', refreshDashboard);
    socketRef.on('absenceUpdated', refreshDashboard);
    socketRef.on('orderFollowupUpdated', refreshDashboard);
    return () => {
      if (refreshTimer.current) clearTimeout(refreshTimer.current);
      socketRef.off('workOrderCreated', refreshDashboard);
      socketRef.off('workOrderUpdated', refreshDashboard);
      socketRef.off('workOrderDeleted', refreshDashboard);
      socketRef.off('productionJobsUpdated', refreshDashboard);
      socketRef.off('absenceUpdated', refreshDashboard);
      socketRef.off('orderFollowupUpdated', refreshDashboard);
    };
  }, [socketRef, token, user?.isAdmin, userPermissions?.canCreateWorkOrder, absenceDate, loadProductionSummary]);

  // Close header popovers when clicking outside
  useEffect(() => {
    if (!showMineMaskiner && !showMittTeam) return;
    const handler = (e) => {
      if (e.target.closest('[data-header-popover]')) return;
      setShowMineMaskiner(false);
      setShowMittTeam(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showMineMaskiner, showMittTeam]);

  // ===== Admin "view as user" preview: load users list when selector opens =====
  useEffect(() => {
    if (!user?.isAdmin || !showUserSelector || usersList.length > 0) return;
    setUsersLoading(true);
    getAllUsers(token)
      .then((users) => {
        // Sort by first name, exclude the admin themselves from the list
        setUsersList(users
          .filter((u) => u.id !== user?.id)
          .sort((a, b) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`, 'nb')));
      })
      .catch((err) => console.error('Could not load users for selector:', err))
      .finally(() => setUsersLoading(false));
  }, [user?.isAdmin, showUserSelector, usersList.length, token, user?.id]);

  // Close user selector when clicking outside
  useEffect(() => {
    if (!showUserSelector) return;
    const handler = (e) => {
      if (userSelectorRef.current && userSelectorRef.current.contains(e.target)) return;
      setShowUserSelector(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showUserSelector]);

  // Load permissions + teams for the selected preview user
  const handleSelectPreviewUser = async (selectedUser) => {
    setShowUserSelector(false);
    setUserSearchQuery('');
    if (!selectedUser) {
      setPreviewData(null);
      setPreviewError('');
      return;
    }
    setPreviewLoading(true);
    setPreviewError('');
    try {
      const [permissions, teams] = await Promise.all([
        fetchUserPermissionsById(selectedUser.id, token),
        fetchTeamsForUser(selectedUser.id, token).catch(() => []),
      ]);
      setPreviewData({
        user: {
          id: selectedUser.id,
          firstName: selectedUser.firstName,
          lastName: selectedUser.lastName,
          isAdmin: selectedUser.isAdmin,
          email: selectedUser.email,
        },
        permissions,
        teams,
      });
    } catch (err) {
      console.error('Could not load preview data:', err);
      setPreviewError(err?.message || 'Kunne ikke laste brukerdata');
      setPreviewData(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleExitPreview = () => {
    setPreviewData(null);
    setPreviewError('');
    setUserSearchQuery('');
  };

  const todayIso = new Date().toISOString().slice(0, 10);
  const shiftAbsenceDate = (deltaDays) => {
    const [y, m, d] = absenceDate.split('-').map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d));
    dt.setUTCDate(dt.getUTCDate() + deltaDays);
    setAbsenceDate(dt.toISOString().slice(0, 10));
  };
  const absenceDateLabel = (() => {
    if (absenceDate === todayIso) return 'i dag';
    const [y, m, d] = absenceDate.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('nb-NO', { day: 'numeric', month: 'short', timeZone: 'UTC' });
  })();

  // Sync sidebar width CSS variable to document root for modals/overlays
  useEffect(() => {
    const updateVars = () => {
      // Fullscreen widgets keep covering the left nav (unchanged behaviour)
      document.documentElement.style.setProperty('--sidebar-width', '0px');
      const isXl = window.innerWidth > 1280;
      const rpWidth = !isXl ? '0px' : rightPanelCollapsed ? '52px' : '300px';
      document.documentElement.style.setProperty('--right-panel-width', rpWidth);
    };
    updateVars();
    window.addEventListener('resize', updateVars);
    return () => window.removeEventListener('resize', updateVars);
  }, [sidebarCollapsed, rightPanelCollapsed, navCollapsed]);

  const handleAlertToggle = async (type) => {
    if (expandedAlert === type) {
      setExpandedAlert(null);
      setAlertOrders([]);
      return;
    }
    setExpandedAlert(type);
    setAlertLoading(true);
    try {
      const orders = await fetchAlertOrders(token, type);
      setAlertOrders(orders);
    } catch {
      setAlertOrders([]);
    }
    setAlertLoading(false);
  };

  const handleStatToggle = async (type) => {
    if (!type) return;
    if (type.startsWith('steel_')) {
      onWidgetSelect('steelPurchases');
      return;
    }
    if (expandedStat === type) {
      setExpandedStat(null);
      setStatItems([]);
      setPendingFollowupOrders([]);
      setRegisteredOrders([]);
      return;
    }
    setExpandedStat(type);
    setStatTab('pending');
    setStatLoading(true);
    try {
      if (type === 'pendingFollowup' || type.startsWith('pendingFollowup_')) {
        const apiUrl = getApiUrl();
        const userIdParam = type === 'pendingFollowup' ? '' : `?userId=${type.replace('pendingFollowup_', '')}`;
        const [pfRes, regRes] = await Promise.all([
          fetch(`${apiUrl}/dashboard/pending-followup${userIdParam}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          }),
          fetch(`${apiUrl}/dashboard/registered-orders${userIdParam}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          }),
        ]);
        setPendingFollowupOrders(pfRes.ok ? (await pfRes.json()) || [] : []);
        setRegisteredOrders(regRes.ok ? (await regRes.json()) || [] : []);
      } else {
        const items = type === 'notifications'
          ? await fetchMyNotifications(token, true)
          : await fetchAlertOrders(token, type);
        setStatItems(items || []);
      }
    } catch {
      setStatItems([]);
      setPendingFollowupOrders([]);
      setRegisteredOrders([]);
    }
    setStatLoading(false);
  };

  // Debounced order search + click-outside to close dropdown
  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    const q = searchQuery.trim();
    if (q.length < 2) {
      setSearchOrders([]);
      setSearchOrdersLoading(false);
      return;
    }
    setSearchOrdersLoading(true);
    searchDebounceRef.current = setTimeout(async () => {
      try {
        const results = await searchWorkOrders(q, token);
        setSearchOrders(results);
      } catch {
        setSearchOrders([]);
      }
      setSearchOrdersLoading(false);
    }, 300);
    return () => clearTimeout(searchDebounceRef.current);
  }, [searchQuery, token]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchBoxRef.current && !searchBoxRef.current.contains(e.target)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const handleClickOutsideStat = (e) => {
      if (statsGridRef.current && !statsGridRef.current.contains(e.target)) {
        setExpandedStat(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutsideStat);
    return () => document.removeEventListener('mousedown', handleClickOutsideStat);
  }, []);

  const searchMatchedModules = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (q.length < 1) return [];
    return widgets
      .filter(w => w.title?.toLowerCase().includes(q) || w.description?.toLowerCase().includes(q))
      .slice(0, 6);
  }, [searchQuery, widgets]);

  const handleSearchResultClick = (widgetId, orderId) => {
    if (orderId) {
      sessionStorage.setItem('selectedWorkOrderId', orderId);
      handleWidgetClick('ordersFlow');
    } else {
      handleWidgetClick(widgetId);
    }
    setSearchOpen(false);
    setSearchQuery('');
  };

  const isAdmin = effectiveUser?.isAdmin;
  const canCreateOrder = effectivePermissions?.canCreateWorkOrder;

  const handleForceRotation = async (teamId) => {
    setAdvancingRotation(teamId);
    try {
      const res = await fetch(`${getApiUrl()}/team-leader-rotation/${teamId}/advance`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const result = await res.json();
        // Refresh rotation data
        fetch(`${getApiUrl()}/team-leader-rotation/current`, {
          headers: { Authorization: `Bearer ${token}` },
        }).then(r => r.ok ? r.json() : []).then(setRotationLeaders).catch(() => {});
      } else {
        const d = await res.json();
        console.error('Force rotation failed:', d.message);
      }
    } catch (e) {
      console.error('Force rotation error:', e);
    }
    setAdvancingRotation(null);
  };
  const visibleLinks = quickLinks.filter(link => {
    if (link.permission_level === 'all') return true;
    if (link.permission_level === 'can_create_order') return isAdmin || canCreateOrder;
    return isAdmin;
  });

  // Apply admin-configured category / sort order / extra visibility restriction on top
  // of the already permission-filtered `widgets` list. This can only further restrict
  // visibility, never grant access beyond what the hardcoded permission checks allow.
  const groupedWidgets = useMemo(() => {
    const configMap = {};
    widgetConfigs.forEach((c) => { configMap[c.widgetId] = c; });

    const withMeta = widgets
      .map((w, index) => {
        const cfg = configMap[w.id];
        const catalogEntry = getCatalogEntry(w.id);
        return {
          ...w,
          category: cfg?.category || catalogEntry?.defaultCategory || 'Annet',
          sortOrder: cfg ? cfg.sortOrder : index,
          isVisible: cfg ? cfg.isVisible : true,
          visibilityLevel: cfg?.visibilityLevel || 'all',
          allowedUserIds: cfg?.allowedUserIds || [],
        };
      })
      .filter((w) => w.isVisible)
      .filter((w) => passesVisibilityLevel(w.visibilityLevel, { user: effectiveUser, userPermissions: effectivePermissions, allowedUserIds: w.allowedUserIds }));

    const groups = {};
    withMeta.forEach((w) => {
      const cat = w.category || 'Annet';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(w);
    });

    Object.values(groups).forEach((list) => {
      list.sort((a, b) => a.sortOrder - b.sortOrder || a.title.localeCompare(b.title, 'nb'));
    });

    const orderMap = {};
    categoryOrders.forEach((c) => { orderMap[c.name] = c.sortOrder; });

    const categoryNames = sortCategoryNames(Object.keys(groups), orderMap);
    return categoryNames.map((name) => ({ name, items: groups[name] }));
  }, [widgets, widgetConfigs, categoryOrders, effectiveUser, effectivePermissions]);

  const radialCategories = useMemo(() => {
    return groupedWidgets.map(g => ({
      id: g.name,
      label: g.name,
      items: g.items.map(w => ({
        id: w.id,
        label: w.title,
        category: g.name,
      })),
    }));
  }, [groupedWidgets]);

  const handleWidgetClick = (widgetId) => {
    // Read-only mode while previewing another user's view
    if (isPreviewing) return;
    if (widgetId === 'alleAnsatte') {
      setShowFellesKalender(true);
      return;
    }
    if (widgetId === 'unimicroOrders') {
      setShowUnimicroOrders(true);
      return;
    }
    onWidgetSelect?.(widgetId);
  };

  // Show pending approval message if user has no widgets (guest)
  if (widgets.length === 0) {
    return (
      <div className="tw-flex tw-items-center tw-justify-center tw-w-full tw-h-full tw-bg-slate-50">
        <div className="tw-text-center tw-p-8">
          <div className="tw-text-6xl tw-mb-4">⏳</div>
          <h2 className="tw-text-2xl tw-font-bold tw-text-slate-800 tw-mb-2">Venter på godkjenning</h2>
          <p className="tw-text-slate-500 tw-mb-1">Din bruker er opprettet, men har ingen tilgang ennå.</p>
          <p className="tw-text-slate-400 tw-text-sm">Vennligst vent til en administrator har tildelt deg en rolle og rettigheter.</p>
          <div className="tw-mt-6 tw-inline-block tw-px-4 tw-py-2 tw-bg-slate-100 tw-rounded-lg tw-border tw-border-slate-200 tw-text-sm tw-text-slate-600">
            👤 Status: <strong>Guest</strong> — Ingen rettigheter
          </div>
        </div>
      </div>
    );
  }

  // Stats cards data
  const fmtNOK = (val) => new Intl.NumberFormat('nb-NO', { style: 'currency', currency: 'NOK', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(val || 0);

  // Note: while previewing another user, `stats` still holds the admin's data, so the
  // "data exists" fallback must not grant access — gate strictly on effective permissions.
  const pendingFollowupCards = (isAdmin || canCreateOrder || (!isPreviewing && stats?.pendingFollowupByUser && stats.pendingFollowupByUser.length > 0))
    ? (stats?.pendingFollowupByUser && stats.pendingFollowupByUser.length > 0
      ? [
          // Company total card
          {
            label: 'Totalt firma',
            value: fmtNOK(stats.pendingFollowupByUser.reduce((sum, u) => sum + (u.total || 0), 0)),
            sub: 'Venter oppfølging',
            accent: '#7C3AED', iconBg: '#EDE9FE', iconColor: '#7C3AED', subColor: '#B7791F',
            icon: FiTrendingUp,
            type: 'pendingFollowup',
            registeredCount: (stats?.registeredByUser || []).reduce((sum, r) => sum + (r.count || 0), 0),
            registeredNOK: (stats?.registeredByUser || []).reduce((sum, r) => sum + (r.totalNOK || 0), 0),
          },
          // Per-user cards
          ...stats.pendingFollowupByUser.map(u => {
            const regData = stats?.registeredByUser?.find(r => r.userId === u.userId);
            return {
              label: u.name,
              value: fmtNOK(u.total),
              sub: 'Venter oppfølging',
              accent: '#E8622C', iconBg: '#FDEEE7', iconColor: '#E8622C', subColor: '#B7791F',
              icon: FiDollarSign,
              type: `pendingFollowup_${u.userId}`,
              userId: u.userId,
              registeredCount: regData?.count || 0,
              registeredNOK: regData?.totalNOK || 0,
            };
          }),
        ]
      : [{
          label: 'Venter oppfølging',
          value: stats?.pendingFollowupTotal != null ? fmtNOK(stats.pendingFollowupTotal) : '—',
          sub: `${effectiveUser?.firstName || ''} ${effectiveUser?.lastName || ''}`.trim() || null,
          accent: '#E8622C', iconBg: '#FDEEE7', iconColor: '#E8622C', subColor: '#B7791F',
          icon: FiDollarSign, type: 'pendingFollowup',
          registeredCount: stats?.registeredCount || 0,
          registeredNOK: stats?.registeredTotalNOK || 0,
        }])
    : [];

  const statsCards = [
    { label: 'Aktive ordre', value: stats?.activeOrders ?? '—', sub: stats?.overdueOrders ? `${stats.overdueOrders} forsinket` : null, accent: '#3E6FA3', iconBg: '#EAF1F8', iconColor: '#3E6FA3', subColor: '#C63D3D', icon: FiFileText, type: 'active' },
    { label: 'I produksjon', value: stats?.inProduction ?? '—', sub: null, accent: '#1E8A5F', iconBg: '#E8F5EF', iconColor: '#1E8A5F', subColor: '#1E8A5F', icon: FiActivity, type: 'production' },
    { label: 'Frister i dag', value: stats?.deadlinesToday ?? '—', sub: stats?.urgentDeadlines ? `${stats.urgentDeadlines} haster` : null, accent: '#B7791F', iconBg: '#FBF1DF', iconColor: '#B7791F', subColor: '#C63D3D', icon: FiClock, type: 'deadlines' },
    { label: 'Nye varsler', value: stats?.unreadNotifications ?? '—', sub: stats?.stoppedOrders ? `${stats.stoppedOrders} stoppet` : null, accent: '#C63D3D', iconBg: '#FBEBEB', iconColor: '#C63D3D', subColor: '#C63D3D', icon: FiBell, type: 'notifications' },
    ...pendingFollowupCards,
    ...rotationLeaders.map(r => ({
      label: r.teamName,
      value: r.currentLeader,
      sub: r.nextLeader ? `Neste: ${r.nextLeader}` : `Uke ${r.weekNumber}/${r.totalWeeks}`,
      accent: '#4338CA', iconBg: '#E0E7FF', iconColor: '#4338CA', subColor: '#6366F1',
      icon: FiUsers,
      type: `rotation_${r.teamId}`,
      rotationData: r,
    })),
  ];

  // Quick actions for right sidebar — defaults if no saved preferences
  const defaultQuickActionIds = ['workOrders', 'ordersFlow', 'absenceRequest', 'alleAnsatte'];
  const activeActionIds = savedQuickActions !== null ? savedQuickActions : defaultQuickActionIds;

  // Build quick actions from widget definitions, filtered by user access and saved order
  const accessibleWidgetIds = widgets.map(w => w.id);
  const quickActions = activeActionIds
    .filter(wid => accessibleWidgetIds.includes(wid))
    .map(wid => {
      const widget = widgets.find(w => w.id === wid);
      if (!widget) return null;
      return {
        label: widget.title,
        icon: getWidgetIcon(wid),
        widgetId: wid,
        show: true,
      };
    })
    .filter(Boolean);

  // All widgets the user can choose from (for the settings panel)
  const availableWidgetsForActions = widgets.map(w => ({
    id: w.id,
    title: w.title,
    icon: getWidgetIcon(w.id),
  }));

  const handleSaveQuickActions = async () => {
    setSavingActions(true);
    try {
      await saveUserQuickActions(token, editingActionIds);
      setSavedQuickActions(editingActionIds);
      setEditingQuickActions(false);
    } catch (err) {
      console.error('Failed to save quick actions:', err);
    } finally {
      setSavingActions(false);
    }
  };

  const handleStartEditingQuickActions = () => {
    setEditingActionIds([...activeActionIds]);
    setEditingQuickActions(true);
  };

  const toggleActionSelection = (wid) => {
    setEditingActionIds(prev =>
      prev.includes(wid) ? prev.filter(id => id !== wid) : [...prev, wid]
    );
  };

  const moveActionUp = (idx) => {
    setEditingActionIds(prev => {
      if (idx === 0) return prev;
      const arr = [...prev];
      [arr[idx - 1], arr[idx]] = [arr[idx], arr[idx - 1]];
      return arr;
    });
  };

  const moveActionDown = (idx) => {
    setEditingActionIds(prev => {
      if (idx === prev.length - 1) return prev;
      const arr = [...prev];
      [arr[idx], arr[idx + 1]] = [arr[idx + 1], arr[idx]];
      return arr;
    });
  };

  // ===== Derived dashboard data (presentation only) =====
  const psum = productionSummary;
  const fmtInt = (v) => (v == null ? '—' : new Intl.NumberFormat('nb-NO').format(v));

  // Machine utilisation for today: running hours vs. elapsed shift hours (07:00–15:30).
  const shiftStart = 7, shiftEnd = 15.5;
  const nowH = now.getHours() + now.getMinutes() / 60;
  const elapsedShift = Math.max(0.25, Math.min(shiftEnd, nowH) - shiftStart);
  const machineCards = (machineHours || []).map((m) => {
    const hasToday = (m.todayReadings || 0) > 0 && nowH >= shiftStart;
    const util = hasToday
      ? Math.min(100, Math.round((m.todayHours / elapsedShift) * 100))
      : (m.yesterdayUtilization != null ? Math.round(m.yesterdayUtilization) : null);
    return { ...m, utilization: util, utilIsToday: hasToday };
  });
  const machinesWithUtil = machineCards.filter((m) => m.utilization != null);
  const oee = machinesWithUtil.length ? Math.round(machinesWithUtil.reduce((s, m) => s + m.utilization, 0) / machinesWithUtil.length) : null;
  const machinesRunning = machineCards.filter((m) => m.currentState === 'RUNNING').length;
  const canSeeMachines = (isAdmin || canCreateOrder) && machineCards.length > 0;

  const openOrdersFlowWithFilter = (filter) => {
    if (isPreviewing) return;
    sessionStorage.removeItem('selectedWorkOrderId');
    sessionStorage.setItem('ordreflytt_initial_filter', filter);
    handleWidgetClick('ordersFlow');
  };
  const openOrder = (id) => {
    if (isPreviewing) return;
    sessionStorage.setItem('selectedWorkOrderId', id);
    handleWidgetClick('ordersFlow');
  };

  // Primary KPI row (5 cards)
  const kpiPrimary = [
    {
      key: 'production', label: 'Ordrer i produksjon', tone: 'primary', icon: FiActivity,
      value: fmtInt(psum ? psum.inProgress : stats?.inProduction),
      subtext: stats?.activeOrders != null ? `${stats.activeOrders} aktive ordre` : null,
      onClick: () => handleStatToggle('production'), open: expandedStat === 'production',
    },
    {
      key: 'planned', label: 'Planlagte ordre', tone: 'blue', icon: FiCalendar,
      value: fmtInt(psum?.planned),
      delta: psum ? { value: (psum.createdToday || 0) - (psum.createdYesterday || 0), label: 'nye vs. i går' } : null,
      onClick: () => handleStatToggle('active'), open: expandedStat === 'active',
    },
    {
      key: 'completed', label: 'Ferdige ordre i dag', tone: 'success', icon: FiCheck,
      value: fmtInt(psum?.completedToday),
      delta: psum ? { value: (psum.completedToday || 0) - (psum.completedYesterday || 0), label: 'vs. i går' } : null,
      onClick: () => openOrdersFlowWithFilter('fullført'),
    },
    {
      key: 'stopped', label: 'Stopper / Avvik', tone: 'danger', icon: FiAlertTriangle,
      value: fmtInt(stats?.stoppedOrders ?? psum?.stopped),
      subtext: stats?.overdueOrders ? `${stats.overdueOrders} forsinket` : 'Ingen forsinkelser',
      subtone: stats?.overdueOrders ? 'warn' : undefined,
      onClick: () => handleStatToggle('stopped'), open: expandedStat === 'stopped',
    },
    {
      key: 'oee', label: 'OEE · Maskinutnyttelse', tone: utilizationTone(oee), icon: FiCpu,
      value: oee != null ? `${oee}%` : '—',
      subtext: machineCards.length ? `${machinesRunning}/${machineCards.length} maskiner kjører` : 'Ingen maskindata',
      onClick: widgets.some((w) => w.id === 'machineMonitor') ? () => handleWidgetClick('machineMonitor') : undefined,
    },
  ];

  const visibleKpiPrimary = kpiPrimary.filter((k) => isKpiCardVisible(kpiCardVisibility, `dash_${k.key}`));

  // Secondary compact stats (existing functionality: deadlines, notifications, follow-up money, rotation)
  const kpiSecondary = statsCards
    .filter((s) => !['active', 'production'].includes(s.type))
    .filter((s) => isKpiCardVisible(kpiCardVisibility, secondaryStatVisibilityKey(s.type)));

  // Left navigation: fixed items mapped to the first accessible widget
  const accessible = (ids) => ids.find((id) => widgets.some((w) => w.id === id));
  const navDefs = [
    { id: 'produksjon', label: 'Produksjon', icon: FiTool, candidates: ['productionPlan', 'jobQueue', 'machineMonitor', 'operatorWork'] },
    { id: 'ordre', label: 'Ordre', icon: FiFileText, candidates: ['ordersFlow', 'workOrdersList', 'workOrders', 'foresporsler'], count: stats?.activeOrders },
    { id: 'lager', label: 'Lager', icon: FiArchive, candidates: ['steelInventory', 'storageLocations', 'steelCertificates', 'forbruksskap', 'machiningTools'] },
    { id: 'kunder', label: 'Kunder', icon: FiUsers, candidates: ['foresporsler', 'unimicroOrders', 'unimicrLookup'], count: assignedInquiriesCount },
    { id: 'leverandorer', label: 'Leverandører', icon: FiTruck, candidates: ['steelPurchases', 'incomingDelivery'] },
    { id: 'rapporter', label: 'Rapporter', icon: FiBarChart2, candidates: ['statistics', 'kpiDashboard', 'dailyReport', 'salesProgress', 'auditLog'] },
    { id: 'innstillinger', label: 'Innstillinger', icon: FiSettings, candidates: ['adminPanel', 'configuration', 'widgetManager', 'quickLinks'] },
  ];
  const navItems = [
    { id: 'hjem', label: 'Hjem', icon: FiHome, active: true, onClick: () => mainRef.current?.scrollTo({ top: 0, behavior: 'smooth' }) },
    ...navDefs.map((d) => ({ ...d, target: accessible(d.candidates) })).filter((d) => d.target).map((d) => ({
      id: d.id, label: d.label, icon: d.icon, count: d.count, onClick: () => handleWidgetClick(d.target),
    })),
  ];
  const navGroups = groupedWidgets.length ? [{
    label: 'Moduler',
    items: groupedWidgets.map((g) => ({
      id: `cat-${g.name}`, label: g.name, icon: getCategoryIconComponent(g.name), count: g.items.length,
      onClick: () => document.getElementById(`cat-${g.name}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
    })),
  }] : [];

  const donutSegments = [
    { label: 'Planlagte ordre', value: psum?.planned || 0, tone: 'blue' },
    { label: 'Pågående ordre', value: psum?.inProgress || 0, tone: 'primary' },
    { label: 'Ferdige i dag', value: psum?.completedToday || 0, tone: 'success' },
    { label: 'Stopper', value: psum?.stopped || 0, tone: 'danger' },
  ];
  const donutTotal = donutSegments.reduce((s, x) => s + x.value, 0);
  const donutActive = (psum?.inProgress || 0) + (psum?.completedToday || 0);
  const donutPct = donutTotal ? Math.round((donutActive / donutTotal) * 100) : 0;

  const roleLabel = effectivePermissions?.roleName || (effectiveUser?.isAdmin ? 'Administrator' : 'Operatør');
  const dateLine = now.toLocaleDateString('nb-NO', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }).replace(/^./, (c) => c.toUpperCase());
  const timeLine = now.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' });
  const fmtTime = (iso) => { if (!iso) return ''; const d = new Date(iso); return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' }); };

  const renderStatDropdown = (type) => {
    if (expandedStat !== type) return null;
    const isFollowup = type === 'pendingFollowup' || type.startsWith('pendingFollowup_');
    return (
      <div className="dsh-dropdown" onClick={(e) => e.stopPropagation()}>
        {statLoading ? (
          <p className="dsh-empty">Laster...</p>
        ) : isFollowup ? (
          <>
            <div className="dsh-dropdown__tabs">
              <button type="button" onClick={() => setStatTab('pending')} className={`dsh-dropdown__tab ${statTab === 'pending' ? 'is-active' : ''}`}>Oppfølging ({pendingFollowupOrders.length})</button>
              <button type="button" onClick={() => setStatTab('registered')} className={`dsh-dropdown__tab ${statTab === 'registered' ? 'is-active' : ''}`}>Registrert ({registeredOrders.length})</button>
            </div>
            {(statTab === 'pending' ? pendingFollowupOrders : registeredOrders).length === 0 ? (
              <p className="dsh-empty">Ingen treff</p>
            ) : (statTab === 'pending' ? pendingFollowupOrders : registeredOrders).map((o, idx) => (
              <div key={idx} className="dsh-dropdown__row" style={{ cursor: 'default' }}>
                <span className="dsh-mono" style={{ color: statTab === 'pending' ? '#fbbf24' : '#7dd3fc', fontWeight: 700, width: 64, flexShrink: 0 }}>{o.orderNumber}</span>
                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--dsh-text-dim)' }}>{o.customer || '—'}</span>
                {statTab === 'pending' && o.deliveryDate && <span style={{ color: 'var(--dsh-text-faint)', fontSize: 10.5 }}>{o.deliveryDate}</span>}
                {statTab === 'registered' && <span className="dsh-mono" style={{ color: '#7dd3fc', fontWeight: 600 }}>{fmtNOK(o.totalNOK)}</span>}
              </div>
            ))}
          </>
        ) : statItems.length === 0 ? (
          <p className="dsh-empty">Ingen treff</p>
        ) : type === 'notifications' ? (
          statItems.map((n) => (
            <button key={n.id} type="button" className="dsh-dropdown__row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 2 }} onClick={() => { setExpandedStat(null); handleWidgetClick('varslinger'); }}>
              <span style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{n.message}</span>
              {n.orderNumber && <span style={{ color: 'var(--dsh-text-faint)', fontSize: 10.5 }}>{n.orderNumber} · {n.customer}</span>}
            </button>
          ))
        ) : (
          statItems.map((o) => (
            <button key={o.rowKey || o.id} type="button" className="dsh-dropdown__row" onClick={() => { setExpandedStat(null); openOrder(o.id); }}>
              <span className="dsh-mono" style={{ color: '#7dd3fc', fontWeight: 700, width: 60, flexShrink: 0 }}>{o.orderNumber}</span>
              <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--dsh-text-dim)' }}>{o.customer}</span>
              {o.teamName && <span style={{ color: 'var(--dsh-text-faint)', fontSize: 10.5, maxWidth: 80, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.teamName}</span>}
              {o.teamStatus && <StatusBadge tone="success" dot={false}>{o.teamStatus}</StatusBadge>}
              {o.stopReason && <span style={{ color: '#fca5a5', fontSize: 10.5 }}>{o.stopReason}</span>}
              {o.daysOverdue != null && <span style={{ color: '#fbbf24', fontSize: 10.5 }}>{o.daysOverdue}d</span>}
            </button>
          ))
        )}
      </div>
    );
  };

  const renderAlertList = (type) => {
    if (expandedAlert !== type) return null;
    return (
      <div className="dsh-alert__list">
        {alertLoading ? <p className="dsh-empty">Laster...</p>
          : alertOrders.length === 0 ? <p className="dsh-empty">Ingen ordre funnet</p>
          : alertOrders.map((o) => (
            <button key={o.id} type="button" className="dsh-dropdown__row" onClick={() => openOrder(o.id)}>
              <span className="dsh-mono" style={{ color: type === 'stopped' ? '#f87171' : '#fbbf24', fontWeight: 700, width: 60, flexShrink: 0 }}>{o.orderNumber}</span>
              <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.customer}</span>
              {o.stopReason && <span style={{ color: 'var(--dsh-text-faint)', fontSize: 10.5, maxWidth: 90, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.stopReason}</span>}
              {o.daysOverdue != null && <span style={{ color: '#fbbf24', fontSize: 10.5 }}>{o.daysOverdue}d</span>}
            </button>
          ))}
      </div>
    );
  };

  const capColor = (available, sold) => {
    if (!sold) return tone('success').color;
    const r = sold / available;
    return r <= 0.6 ? tone('success').color : r <= 0.85 ? tone('warning').color : r <= 1 ? '#f97316' : tone('danger').color;
  };

  return (
    <div className="dsh-root" style={{ '--sidebar-width': '0px' }}>
      {/* ===== TOP HEADER ===== */}
      <header
        className={`dsh-header ${headerBgUrl ? 'dsh-header--image' : ''}`}
        style={headerBgUrl ? { backgroundImage: `linear-gradient(rgba(15,23,42,0.82), rgba(15,23,42,0.82)), url(${getServerUrl()}${headerBgUrl})` } : undefined}
      >
        <div className="dsh-brand">
          {logoUrl ? (
            <img src={`${getServerUrl()}${logoUrl}`} alt={companyName || 'Logo'} className="dsh-brand__logo" />
          ) : (
            <div className="dsh-brand__mark">{(companyName || 'PS').slice(0, 2).toUpperCase()}</div>
          )}
          <div style={{ minWidth: 0 }}>
            <h2 className="dsh-brand__name" style={headerTextColor ? { color: headerTextColor } : undefined}>{companyName || 'Produksjonsstyring'}</h2>
            <p className="dsh-brand__sub" style={headerTextColor ? { color: headerTextColor, opacity: 0.7 } : undefined}>Produksjonsstyring &amp; ERP</p>
          </div>
          <button type="button" onClick={() => setShowSpaceInvaders(true)} title="Space Invaders 🎮" className="dsh-iconbtn dsh-iconbtn--ghost" style={{ height: 30, minWidth: 30, marginLeft: 4 }}>🎮</button>
        </div>

        {/* Global search */}
        <div ref={searchBoxRef} style={{ position: 'relative', flex: '0 1 auto' }}>
          <div className="dsh-search">
            <FiSearch size={15} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setSearchOpen(true); }}
              onFocus={() => setSearchOpen(true)}
              placeholder="Søk i moduler, ordre, kunder…"
            />
            {searchQuery ? (
              <button type="button" onClick={() => { setSearchQuery(''); setSearchOrders([]); }} className="dsh-iconbtn" style={{ height: 22, minWidth: 22, padding: 0 }}>✕</button>
            ) : <kbd>/</kbd>}
          </div>
          {searchOpen && searchQuery.trim().length > 0 && (
            <div className="dsh-pop" style={{ left: 0, right: 0, width: '100%', maxHeight: 380, overflowY: 'auto' }}>
              {searchMatchedModules.length === 0 && searchOrders.length === 0 && !searchOrdersLoading && (
                <p className="dsh-empty" style={{ textAlign: 'center', padding: 20 }}>Ingen treff for "{searchQuery}"</p>
              )}
              {searchMatchedModules.length > 0 && (
                <div>
                  <div className="dsh-pop__head">Moduler</div>
                  {searchMatchedModules.map((w) => (
                    <button key={w.id} type="button" onClick={() => handleSearchResultClick(w.id)} className="dsh-pop__item" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
                      <span style={{ fontWeight: 600 }}>{w.title}</span>
                      <span style={{ fontSize: 11, color: 'var(--dsh-text-faint)' }}>{w.description}</span>
                    </button>
                  ))}
                </div>
              )}
              {(searchOrders.length > 0 || searchOrdersLoading) && (
                <div>
                  <div className="dsh-pop__head">Ordre / Kunde</div>
                  {searchOrdersLoading && <p className="dsh-empty">Søker...</p>}
                  {!searchOrdersLoading && searchOrders.map((o) => (
                    <button key={o.id} type="button" onClick={() => handleSearchResultClick(null, o.id)} className="dsh-pop__item">
                      <span className="dsh-mono" style={{ color: '#7dd3fc', fontWeight: 700 }}>{o.orderNumber}</span>
                      <span style={{ color: 'var(--dsh-text-dim)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.customer}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="dsh-header__actions">
          {/* Mine Maskiner */}
          {myMachineJobs.length > 0 && (
            <div style={{ position: 'relative' }} data-header-popover>
              <button type="button" onClick={() => { setShowMineMaskiner(!showMineMaskiner); setShowMittTeam(false); }} className={`dsh-iconbtn ${showMineMaskiner ? 'is-active' : ''}`} title="Mine Maskiner">
                <FiTool size={17} />
                <span className="dsh-badge-dot dsh-badge-dot--blue">{myMachineJobs.length}</span>
              </button>
              {showMineMaskiner && (
                <div className="dsh-pop" style={{ width: 340, maxHeight: 480, overflowY: 'auto' }}>
                  <div className="dsh-pop__head"><FiTool size={14} /> Mine Maskiner <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, marginLeft: 4 }}>Nåværende og neste jobb</span></div>
                  <div style={{ padding: 10, display: 'grid', gap: 8 }}>
                    {myMachineJobs.map((m) => (
                      <div key={m.id} className="dsh-card" style={{ overflow: 'hidden' }}>
                        <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--dsh-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: 13, fontWeight: 700 }}>🔧 {m.name}</span>
                          {m.teamName && <span style={{ fontSize: 10.5, color: 'var(--dsh-text-faint)' }}>{m.teamName}</span>}
                        </div>
                        {m.current && (
                          <button type="button" className="dsh-pop__item" style={{ background: 'rgba(34,197,94,0.06)', flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}
                            onClick={() => { if (m.current.work_order_id) sessionStorage.setItem('selectedWorkOrderId', m.current.work_order_id); handleWidgetClick('ordersFlow'); setShowMineMaskiner(false); }}>
                            <StatusBadge tone="success" pulse>Nåværende</StatusBadge>
                            <span style={{ fontSize: 13, fontWeight: 600 }}>{m.current.order_number} — {m.current.customer || ''}</span>
                            {m.current.memo && <span style={{ fontSize: 11, color: 'var(--dsh-text-dim)' }}>{m.current.memo}</span>}
                          </button>
                        )}
                        {m.next && (
                          <button type="button" className="dsh-pop__item" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}
                            onClick={() => { if (m.next.work_order_id) sessionStorage.setItem('selectedWorkOrderId', m.next.work_order_id); handleWidgetClick('ordersFlow'); setShowMineMaskiner(false); }}>
                            <StatusBadge tone="primary">Neste</StatusBadge>
                            <span style={{ fontSize: 13, fontWeight: 500 }}>{m.next.order_number} — {m.next.customer || ''}</span>
                            {m.next.memo && <span style={{ fontSize: 11, color: 'var(--dsh-text-dim)' }}>{m.next.memo}</span>}
                          </button>
                        )}
                        {!m.current && !m.next && <p className="dsh-empty">Ingen aktive jobber</p>}
                        {m.totalActive > 2 && <div style={{ padding: '6px 12px', fontSize: 10.5, color: 'var(--dsh-text-faint)', textAlign: 'right', borderTop: '1px solid var(--dsh-border)' }}>+{m.totalActive - 2} flere i kø</div>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Mitt Team */}
          {(isAdmin || canCreateOrder) && (
            <div style={{ position: 'relative' }} data-header-popover>
              <button type="button" onClick={() => { setShowMittTeam(!showMittTeam); setShowMineMaskiner(false); }} className={`dsh-iconbtn ${showMittTeam ? 'is-active' : ''}`} title="Mitt Team">
                <FiUsers size={17} />
              </button>
              {showMittTeam && (
                <div className="dsh-pop dsh-embed" style={{ width: 520, maxHeight: 600, overflowY: 'auto' }}>
                  <div className="dsh-pop__head"><FiUsers size={14} /> Mitt Team</div>
                  <div style={{ padding: '0 4px 4px' }}>
                    <TeamInfoPanel token={token} onOpenWidget={(id) => { handleWidgetClick(id); setShowMittTeam(false); }} refreshTrigger={refreshTrigger} />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Radial quick menu */}
          <button type="button" onClick={() => setShowRadialDial(true)} className="dsh-iconbtn" title="Åpne hurtigmeny"><FiRefreshCw size={16} /></button>

          {/* Notifications */}
          <button type="button" onClick={() => handleWidgetClick('varslinger')} className="dsh-iconbtn" title="Varslinger">
            <FiBell size={17} />
            {unreadNotificationsCount > 0 && <span className="dsh-badge-dot">{unreadNotificationsCount > 99 ? '99+' : unreadNotificationsCount}</span>}
          </button>

          {/* Chat */}
          {setShowChat && (
            <div
              style={{ position: 'relative' }}
              onMouseEnter={() => { if (onlineUsersTimeoutRef?.current) { clearTimeout(onlineUsersTimeoutRef.current); onlineUsersTimeoutRef.current = null; } setShowOnlineUsers?.(true); }}
              onMouseLeave={() => { if (onlineUsersTimeoutRef) { onlineUsersTimeoutRef.current = setTimeout(() => setShowOnlineUsers?.(false), 1000); } }}
            >
              <button
                type="button"
                onClick={() => { setShowChat(!showChat); setShowOnlineUsers?.(false); if (!showChat) { setUnreadChatCount?.(0); setDmUser?.(null); } }}
                className={`dsh-iconbtn ${showChat ? 'is-active' : ''}`}
                title="Chat"
              >
                <FiMessageSquare size={16} />
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11.5, fontWeight: 700, padding: '1px 7px', borderRadius: 999, background: 'var(--dsh-surface-2)' }}>
                  <span style={{ width: 6, height: 6, borderRadius: 999, background: '#4ade80' }} />{onlineUsers.length}
                </span>
                {unreadChatCount > 0 && !showChat && <span className="dsh-badge-dot">{unreadChatCount > 99 ? '99+' : unreadChatCount}</span>}
              </button>
              {showChat && (
                <ChatPanel socket={socketRef} currentUser={user} onClose={() => setShowChat(false)} dmUser={dmUser} token={token} />
              )}
              {!showChat && showOnlineUsers && (
                <div className="dsh-pop" style={{ minWidth: 240, maxHeight: 300, overflowY: 'auto' }}>
                  <div className="dsh-pop__head">Påloggede brukere</div>
                  {onlineUsers.length === 0 ? (
                    <p className="dsh-empty" style={{ textAlign: 'center' }}>Ingen andre pålogget</p>
                  ) : onlineUsers.map((u, idx) => (
                    <div key={u.id || idx} className="dsh-pop__item" style={{ cursor: 'default' }}>
                      <span style={{ width: 8, height: 8, borderRadius: 999, background: '#22c55e', flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: 12.5 }}>{u.firstName} {u.lastName}</div>
                        <div style={{ color: 'var(--dsh-text-faint)', fontSize: 11 }}>{u.email}</div>
                      </div>
                      {u.id !== user?.id && (
                        <button type="button" className="dsh-btn dsh-btn--primary dsh-btn--xs" onClick={(e) => { e.stopPropagation(); setDmUser?.({ id: u.id, name: `${u.firstName} ${u.lastName}` }); setShowChat(true); setShowOnlineUsers?.(false); setUnreadChatCount?.(0); }}>
                          <FiMessageSquare size={12} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <WeatherHeaderChip />

          {/* Admin "view as user" */}
          {user?.isAdmin && (
            <div ref={userSelectorRef} style={{ position: 'relative' }} data-header-popover>
              {isPreviewing ? (
                <button type="button" onClick={handleExitPreview} className="dsh-btn" style={{ background: 'var(--dsh-danger)', borderColor: 'var(--dsh-danger)', color: '#fff', height: 36 }} title="Avslutt forhåndsvisning">
                  <FiX size={14} /> <span className="tw-hidden sm:tw-inline">Avslutt forhåndsvisning</span>
                </button>
              ) : (
                <button type="button" onClick={() => setShowUserSelector((v) => !v)} className="dsh-iconbtn" title="Se hovedsiden som en annen bruker">
                  <FiEye size={15} /> <span className="tw-hidden lg:tw-inline" style={{ fontSize: 12.5, fontWeight: 600 }}>Vis som</span> <FiChevronDown size={13} />
                </button>
              )}
              {showUserSelector && !isPreviewing && (
                <div className="dsh-pop" style={{ width: 320, maxHeight: 420 }}>
                  <div style={{ padding: 12, borderBottom: '1px solid var(--dsh-border)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, fontSize: 13, fontWeight: 700 }}><FiEye size={14} /> Forhåndsvis som bruker</div>
                    <p style={{ margin: '0 0 8px', fontSize: 11, color: 'var(--dsh-text-faint)' }}>Se hvilke moduler brukeren har tilgang til.</p>
                    <input type="text" value={userSearchQuery} onChange={(e) => setUserSearchQuery(e.target.value)} placeholder="Søk etter navn…" className="dsh-input" autoFocus />
                  </div>
                  <div style={{ overflowY: 'auto', maxHeight: 300 }}>
                    {usersLoading && <p className="dsh-empty" style={{ textAlign: 'center' }}>Laster brukere…</p>}
                    {!usersLoading && usersList.length === 0 && <p className="dsh-empty" style={{ textAlign: 'center' }}>Ingen brukere funnet.</p>}
                    {!usersLoading && usersList
                      .filter((u) => { const q = userSearchQuery.toLowerCase().trim(); return !q || `${u.firstName} ${u.lastName}`.toLowerCase().includes(q); })
                      .map((u) => (
                        <button key={u.id} type="button" onClick={() => handleSelectPreviewUser(u)} className="dsh-pop__item">
                          <span className="dsh-avatar" style={{ width: 28, height: 28, fontSize: 10 }}>{(u.firstName || 'U').charAt(0)}{(u.lastName || '').charAt(0)}</span>
                          <span style={{ flex: 1, minWidth: 0 }}>
                            <span style={{ display: 'block', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.firstName} {u.lastName}</span>
                            <span style={{ display: 'block', fontSize: 11, color: 'var(--dsh-text-faint)' }}>{u.isAdmin ? 'Administrator' : u.hasEmployee ? 'Operatør' : 'Gjest'}</span>
                          </span>
                        </button>
                      ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* User menu */}
          <div ref={userMenuRef} style={{ position: 'relative' }}>
            <button type="button" className="dsh-user" onClick={() => setShowUserMenu((v) => !v)} title="Brukermeny">
              <span className="dsh-avatar">{(user?.firstName || 'U').charAt(0)}{(user?.lastName || '').charAt(0)}</span>
              <span className="tw-hidden sm:tw-block" style={{ textAlign: 'left' }}>
                <p className="dsh-user__name">{user?.firstName} {user?.lastName}</p>
                <p className="dsh-user__role">{userPermissions?.roleName || (user?.isAdmin ? 'Administrator' : 'Operatør')}</p>
              </span>
              <FiChevronDown size={14} style={{ color: 'var(--dsh-text-faint)', transform: showUserMenu ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
            </button>
            {showUserMenu && (
              <div className="dsh-pop" style={{ width: 220, padding: 6 }}>
                <div style={{ padding: '8px 12px 10px', borderBottom: '1px solid var(--dsh-border)', marginBottom: 4 }}>
                  <div style={{ fontSize: 13, fontWeight: 700 }}>{user?.firstName} {user?.lastName}</div>
                  <div style={{ fontSize: 11, color: 'var(--dsh-text-faint)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.email}</div>
                </div>
                <button type="button" className="dsh-pop__menu-item" onClick={() => { setShowUserMenu(false); handleWidgetClick('varslinger'); }}><FiBell size={14} /> Varslinger</button>
                {widgets.some((w) => w.id === 'absenceRequest') && (
                  <button type="button" className="dsh-pop__menu-item" onClick={() => { setShowUserMenu(false); handleWidgetClick('absenceRequest'); }}><FiCalendar size={14} /> Fraværssøknad</button>
                )}
                {widgets.some((w) => w.id === 'configuration') && (
                  <button type="button" className="dsh-pop__menu-item" onClick={() => { setShowUserMenu(false); handleWidgetClick('configuration'); }}><FiSettings size={14} /> Innstillinger</button>
                )}
                {onSignOut && (
                  <button type="button" className="dsh-pop__menu-item dsh-pop__menu-item--danger" onClick={() => { setShowUserMenu(false); onSignOut(); }}><FiLogOut size={14} /> Logg ut</button>
                )}
              </div>
            )}
          </div>
        </div>
      </header>

      {/* ===== BODY: main + right aside ===== */}
      <div className="dsh-body">
        <main
          ref={mainRef}
          className={`dsh-main ${dashboardBgUrl ? 'dsh-main--image' : ''}`}
          style={dashboardBgUrl ? { '--dsh-main-image': `url(${getServerUrl()}${dashboardBgUrl})` } : undefined}
        >
          {/* Greeting */}
          <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, marginBottom: 16 }}>
            <div>
              <h1 className="dsh-h1" style={greetingTextColor ? { color: greetingTextColor } : undefined}>
                {getGreeting()}, {effectiveUser?.firstName || 'bruker'} 👋
              </h1>
              <p className="dsh-sub" style={greetingTextColor ? { color: greetingTextColor, opacity: 0.75 } : undefined}>
                {dateLine} · <span className="dsh-mono">{timeLine}</span>
              </p>
              {isPreviewing && (
                <p style={{ margin: '4px 0 0', fontSize: 12, color: '#fca5a5', fontWeight: 600 }}>
                  Forhåndsvisning — du ser hovedsiden som <strong>{effectiveUser?.firstName} {effectiveUser?.lastName}</strong> ({roleLabel})
                </p>
              )}
            </div>
          </div>

          {previewLoading && <div className="dsh-banner dsh-banner--info">Laster brukerdata for forhåndsvisning…</div>}
          {previewError && <div className="dsh-banner dsh-banner--danger">{previewError}</div>}
          {isPreviewing && (
            <div className="dsh-banner dsh-banner--warn">
              <span><FiEye size={14} style={{ verticalAlign: -2, marginRight: 6 }} />Skrivebeskyttet forhåndsvisning — moduler kan ikke åpnes. Tilgjengelige moduler for <strong>{effectiveUser?.firstName} {effectiveUser?.lastName}</strong> vises under.</span>
              <button type="button" onClick={handleExitPreview} className="dsh-btn dsh-btn--xs" style={{ background: 'var(--dsh-warning)', borderColor: 'var(--dsh-warning)', color: '#0b1220' }}>Avslutt</button>
            </div>
          )}

          {/* ===== Primary KPI row ===== */}
          <div ref={statsGridRef}>
            {visibleKpiPrimary.length > 0 && (
            <div className="dsh-grid-kpi" style={{ marginBottom: 12 }}>
              {visibleKpiPrimary.map((k) => (
                <div key={k.key} style={{ position: 'relative', display: 'flex', flexDirection: 'column' }}>
                  <KpiCard
                    icon={k.icon} label={k.label} value={k.value} tone={k.tone}
                    delta={k.delta} subtext={k.subtext} subtone={k.subtone}
                    onClick={k.onClick} open={k.open}
                  />
                  {k.open && renderStatDropdown(k.key === 'planned' ? 'active' : k.key)}
                </div>
              ))}
            </div>
            )}

            {/* ===== Secondary compact stats (frister, varsler, oppfølging, rotasjon) ===== */}
            {kpiSecondary.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 10, marginBottom: 14 }}>
                {kpiSecondary.map((s, i) => {
                  const isRotation = s.type.startsWith('rotation_');
                  const isFollowup = s.type === 'pendingFollowup' || s.type.startsWith('pendingFollowup_');
                  const t = s.type === 'deadlines' ? 'warning' : s.type === 'notifications' ? 'danger' : isRotation ? 'secondary' : 'amber';
                  return (
                    <div key={i} style={{ position: 'relative', display: 'flex', flexDirection: 'column' }}>
                      <KpiCard
                        compact icon={s.icon} label={s.label} value={s.value} tone={t}
                        subtext={s.sub} subtone={s.subColor === '#C63D3D' ? 'down' : undefined}
                        onClick={isRotation ? undefined : () => handleStatToggle(s.type)}
                        open={expandedStat === s.type}
                      >
                        {isFollowup && (
                          <>
                            <span className="dsh-kpi__split" />
                            <span style={{ minWidth: 0 }}>
                              <span className="dsh-kpi__label" style={{ color: '#7dd3fc' }}>Registrert</span>
                              <span className="dsh-kpi__value dsh-kpi__value--sm" style={{ display: 'block' }}>{s.registeredCount || 0}</span>
                              <span className="dsh-kpi__delta" style={{ color: '#7dd3fc' }}>{fmtNOK(s.registeredNOK)}</span>
                            </span>
                          </>
                        )}
                        {isRotation && (isAdmin || (s.rotationData?.memberUserIds?.includes(user?.id))) && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); handleForceRotation(s.rotationData.teamId); }}
                            disabled={advancingRotation === s.rotationData.teamId}
                            className="dsh-btn dsh-btn--primary dsh-btn--xs"
                            title="Tving rotasjon"
                          >
                            <FiRefreshCw size={11} className={advancingRotation === s.rotationData.teamId ? 'tw-animate-spin' : ''} />
                          </button>
                        )}
                      </KpiCard>
                      {!isRotation && renderStatDropdown(s.type)}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* ===== Steel pricing strip ===== */}
          {isKpiCardVisible(kpiCardVisibility, 'dash_steelPrices') && (effectiveUser?.isAdmin || effectivePermissions?.canCreateWorkOrder) && (isPreviewing || (steelSummary && steelSummary.grades && steelSummary.grades.length > 0)) && (
            <button type="button" onClick={() => onWidgetSelect('steelPurchases')} className="dsh-card dsh-card--click dsh-strip" style={{ marginBottom: 12 }}>
              <span className="dsh-card__accent" style={{ background: tone('amber').color }} />
              <span className="dsh-strip__label"><span className="dsh-kpi__icon" style={{ width: 30, height: 30, background: tone('amber').soft, color: tone('amber').color }}><FiShoppingCart size={15} /></span>Stålpriser</span>
              {(isPreviewing ? [] : (steelSummary?.grades || [])).map((g, i) => (
                <span key={i} className="dsh-chip">
                  <span className="dsh-chip__v">{g.steelGrade || 'Ukjent'}</span>
                  <span className="dsh-chip__k">kjøp</span>
                  <span className="dsh-chip__v" style={{ color: '#fbbf24' }}>{g.avgPurchasePricePerKg > 0 ? Number(g.avgPurchasePricePerKg).toFixed(2) : '—'}</span>
                  <span className="dsh-chip__k">salg</span>
                  <span className="dsh-chip__v" style={{ color: '#4ade80' }}>{g.avgSellingPricePerKg > 0 ? Number(g.avgSellingPricePerKg).toFixed(2) : '—'}</span>
                  <span className="dsh-chip__k">kr/kg</span>
                </span>
              ))}
              {isPreviewing && <span style={{ fontSize: 12, color: 'var(--dsh-text-faint)', fontStyle: 'italic' }}>Forhåndsvisning — data ikke lastet</span>}
              <FiChevronRight size={16} style={{ marginLeft: 'auto', color: 'var(--dsh-text-faint)', flexShrink: 0 }} />
            </button>
          )}

          {/* ===== Sales strip ===== */}
          {(() => {
            const _isSalgTL = (effectivePermissions?.teamLeaderTeamNames || []).some((n) => n.toUpperCase().includes('SALG'));
            const _isFakt = (effectiveTeams || []).some((t) => t.name?.toUpperCase().includes('FAKTURERING'));
            const _salesCfg = widgetConfigs.find((c) => c.widgetId === 'salesProgress');
            const _passesWidget = _salesCfg ? passesVisibilityLevel(_salesCfg.visibilityLevel || 'all', { user: effectiveUser, userPermissions: effectivePermissions, allowedUserIds: _salesCfg.allowedUserIds || [] }) : false;
            const canSeeSales = effectiveUser?.isAdmin || _isSalgTL || _isFakt || _passesWidget;
            if (!canSeeSales) return null;
            if (!isKpiCardVisible(kpiCardVisibility, 'dash_sales')) return null;
            if (!salesKpiData && !isPreviewing) return null;
            if (isPreviewing && !salesKpiData) {
              return (
                <div className="dsh-card dsh-strip" style={{ marginBottom: 12 }}>
                  <span className="dsh-card__accent" style={{ background: tone('secondary').color }} />
                  <span className="dsh-strip__label"><span className="dsh-kpi__icon" style={{ width: 30, height: 30, background: tone('secondary').soft, color: tone('secondary').color }}><FiTrendingUp size={15} /></span>Salg</span>
                  <span style={{ fontSize: 12, color: 'var(--dsh-text-faint)', fontStyle: 'italic' }}>Forhåndsvisning — data ikke lastet</span>
                </div>
              );
            }
            if (!salesKpiData) return null;
            const todayNok = salesKpiData.summary?.today?.totalNok || 0;
            const todayCount = salesKpiData.summary?.today?.orderCount || 0;
            const weekNok = salesKpiData.summary?.week?.totalNok || 0;
            const monthNok = salesKpiData.summary?.month?.totalNok || 0;
            const yearNok = salesKpiData.summary?.year?.totalNok || 0;
            const dagSnitt30 = salesKpiData.summary?.dailyAverage || 0;
            const aarSnitt = salesKpiData.summary?.yearWorkdayAverage || 0;
            const tgt = salesKpiData.target || 600000;
            const todayPct = tgt > 0 ? Math.min((todayNok / tgt) * 100, 100) : 0;
            const todayColor = todayPct >= 100 ? tone('success').color : todayPct >= 80 ? tone('warning').color : tone('danger').color;
            const f = (v) => new Intl.NumberFormat('nb-NO', { maximumFractionDigits: 0 }).format(v);
            const topSellersToday = (salesSellerData || []).filter((s) => s.todayNok > 0).sort((a, b) => b.todayNok - a.todayNok).slice(0, 5);
            return (
              <button type="button" onClick={() => onWidgetSelect('salesProgress')} className="dsh-card dsh-card--click dsh-strip" style={{ marginBottom: 12 }}>
                <span className="dsh-card__accent" style={{ background: todayColor }} />
                <span className="dsh-strip__label"><span className="dsh-kpi__icon" style={{ width: 30, height: 30, background: tone('secondary').soft, color: tone('secondary').color }}><FiTrendingUp size={15} /></span>Salg</span>
                <span className="dsh-chip" style={{ gap: 10 }}>
                  <span className="dsh-chip__k">I dag</span>
                  <span className="dsh-chip__v" style={{ color: todayColor }}>{f(todayNok)} kr</span>
                  <span className="dsh-chip__k">({todayCount})</span>
                  <span style={{ width: 70 }}><ProgressBar value={todayPct} color={todayColor} thin /></span>
                  <span className="dsh-chip__v dsh-mono" style={{ color: todayColor, fontSize: 12 }}>{Math.round(todayPct)}%</span>
                </span>
                <span className="dsh-chip"><span className="dsh-chip__k">Uke</span><span className="dsh-chip__v" style={{ color: '#7dd3fc' }}>{f(weekNok)} kr</span></span>
                <span className="dsh-chip"><span className="dsh-chip__k">Måned</span><span className="dsh-chip__v" style={{ color: '#c4b5fd' }}>{f(monthNok)} kr</span></span>
                <span className="dsh-chip"><span className="dsh-chip__k">I år</span><span className="dsh-chip__v">{f(yearNok)} kr</span></span>
                <span className="dsh-chip"><span className="dsh-chip__k">Dagsnitt 30d</span><span className="dsh-chip__v" style={{ color: '#2dd4bf' }}>{f(dagSnitt30)} kr</span></span>
                <span className="dsh-chip"><span className="dsh-chip__k">Årssnitt</span><span className="dsh-chip__v" style={{ color: '#2dd4bf' }}>{f(aarSnitt)} kr</span></span>
                {topSellersToday.map((s, i) => (
                  <span key={s.sellerNr} className="dsh-chip" style={{ borderColor: 'rgba(167,139,250,0.3)' }}>
                    <span className="dsh-chip__k" style={{ color: '#c4b5fd' }}>#{i + 1}</span>
                    <span className="dsh-chip__v" style={{ fontSize: 12.5 }}>{s.sellerName}</span>
                    <span className="dsh-chip__v" style={{ color: '#c4b5fd' }}>{f(s.todayNok)} kr</span>
                    <span className="dsh-chip__k">({s.todayOrders})</span>
                  </span>
                ))}
                <FiChevronRight size={16} style={{ marginLeft: 'auto', color: 'var(--dsh-text-faint)', flexShrink: 0 }} />
              </button>
            );
          })()}

          {/* ===== Maskintimer + Produksjon i dag ===== */}
          {(() => {
            const showMachinePanel = canSeeMachines && isKpiCardVisible(kpiCardVisibility, 'dash_machines');
            const showProductionDonut = isKpiCardVisible(kpiCardVisibility, 'dash_productionToday');
            if (!showMachinePanel && !showProductionDonut) return null;
            return (
          <div className={showMachinePanel ? 'dsh-grid-2' : ''} style={{ marginBottom: 14 }}>
            {showMachinePanel && (
              <section className="dsh-card dsh-card--pad">
                <SectionHeader
                  icon={FiMonitor}
                  title="Maskintimer"
                  meta={`Produksjonstid · ${machinesRunning} av ${machineCards.length} kjører`}
                  right={widgets.some((w) => w.id === 'machineMonitor') && <button type="button" className="dsh-link" onClick={() => handleWidgetClick('machineMonitor')}>Maskinovervåking →</button>}
                />
                <div className="dsh-grid-machines">
                  {machineCards.map((m) => (
                    <MachineCard
                      key={m.id}
                      name={m.name}
                      type={m.machineType || m.teamName || 'Maskin'}
                      hours={m.lastMonthHours}
                      unit="h"
                      utilization={m.utilization}
                      state={m.currentState}
                      sub={<><span>I dag <b style={{ color: 'var(--dsh-text)' }}>{m.todayHours}h</b></span><span>Uke <b style={{ color: 'var(--dsh-text)' }}>{m.lastWeekHours}h</b></span><span>{m.utilIsToday ? 'i dag' : 'i går'}</span></>}
                      title={`${m.name} — I dag: ${m.todayHours}t / I går: ${m.yesterdayHours}t / Siste uke: ${m.lastWeekHours}t / Siste måned: ${m.lastMonthHours}t`}
                      onClick={widgets.some((w) => w.id === 'machineMonitor') ? () => handleWidgetClick('machineMonitor') : undefined}
                    />
                  ))}
                </div>
              </section>
            )}

            {showProductionDonut && (
            <section className="dsh-card dsh-card--pad" style={{ display: 'flex', flexDirection: 'column' }}>
              <SectionHeader icon={FiPieChart} title="Produksjon i dag" meta={psum ? `${donutTotal} ordre` : null} right={<button type="button" className="dsh-link" onClick={() => openOrdersFlowWithFilter('mine-active')}>Ordreflyt →</button>} />
              {psum ? (
                <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                  <DonutChart size={132} stroke={13} segments={donutSegments} centerValue={`${donutPct}%`} centerLabel="i gang / ferdig" />
                </div>
              ) : (
                <p className="dsh-empty">Laster produksjonsoversikt…</p>
              )}
            </section>
            )}
          </div>
            );
          })()}

          {/* ===== Fildeling + Kapasitet ===== */}
          <div className="dsh-row" style={{ marginBottom: 14, flexWrap: 'wrap' }}>
            <div className="dsh-embed" style={{ width: '100%', maxWidth: 420, flexShrink: 0 }}>
              <FileShareQuickCard
                token={token}
                currentUser={user}
                kpiCardBgColor='#111a2e'
                kpiCardTextColor='#e5e9f0'
                onOpenFullWidget={() => onWidgetSelect('fileShare')}
              />
            </div>
            {(effectiveUser?.isAdmin || effectivePermissions?.canCreateWorkOrder) && isKpiCardVisible(kpiCardVisibility, 'dash_capacity') && capacitySummary && (() => {
              const totalColor = capColor(capacitySummary.totalAvailable, capacitySummary.totalSold);
              return (
                <div className="dsh-card dsh-card--click" style={{ flex: 1, minWidth: 320, display: 'flex', padding: 12 }} onClick={() => onWidgetSelect('teamCapacity')}>
                  <span className="dsh-card__accent" style={{ background: totalColor }} />
                  {/* Left column: total KPI + week selector */}
                  <div style={{ width: 150, flexShrink: 0, paddingRight: 12, borderRight: '1px solid var(--dsh-border)', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <span className="dsh-kpi__icon" style={{ width: 26, height: 26, borderRadius: 8, background: tone('success').soft, color: tone('success').color }}><FiBarChart2 size={12} /></span>
                      <span className="dsh-kpi__label">Kapasitet</span>
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: totalColor, lineHeight: 1 }}>{capacitySummary.utilization}%</div>
                    <div style={{ fontSize: 11, color: 'var(--dsh-text-dim)', marginTop: 3 }}><b style={{ color: totalColor }}>{capacitySummary.totalSold}t</b> / {capacitySummary.totalAvailable}t</div>
                    <div className="dsh-seg" style={{ marginTop: 8 }} onClick={(e) => e.stopPropagation()}>
                      {[1, 2, 3, 4].map((w) => (
                        <button key={w} type="button" onClick={() => setCapacityWeeks(w)} className={`dsh-seg__btn ${capacityWeeks === w ? 'is-active' : ''}`}>{w}u</button>
                      ))}
                    </div>
                  </div>
                  {/* Right: team cards filling entire height */}
                  <div style={{ flex: 1, marginLeft: 12, display: 'grid', gap: 8, alignContent: 'stretch', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', minHeight: 0 }}>
                    {capacitySummary.displayItems.map((tc, i) => {
                      const c = capColor(tc.available, tc.sold);
                      const pct = tc.available > 0 ? Math.round((tc.sold / tc.available) * 100) : 0;
                      const grpColor = tc.groupNum ? (capacitySummary.groupColors[tc.groupNum] || '#64748b') : null;
                      return (
                        <div key={i} style={{ padding: 8, borderRadius: 9, background: 'var(--dsh-bg-elev)', border: `1px solid ${grpColor || 'var(--dsh-border)'}`, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 5, minWidth: 0 }}>
                            {tc.groupNum && <span style={{ fontSize: 8, fontWeight: 800, padding: '1px 4px', borderRadius: 4, background: grpColor, color: '#fff' }}>Gr {tc.groupNum}</span>}
                            <span style={{ fontSize: 11, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={tc.teamName}>{tc.teamName}</span>
                          </div>
                          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 4 }}>
                            <ProgressBar value={pct} color={c} />
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--dsh-text-faint)' }}>
                              <span>S <b style={{ color: c }}>{tc.sold}t</b></span><span>T <b style={{ color: 'var(--dsh-text-dim)' }}>{tc.available}t</b></span><span className="dsh-mono" style={{ fontWeight: 700, color: c }}>{pct}%</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
          </div>

          {/* ===== Applikasjoner + Dagens ordre ===== */}
          <div className="dsh-grid-2" style={{ marginBottom: 14, alignItems: 'start' }}>
            <div>
              {groupedWidgets.map(({ name, items }) => (
                <section key={name} id={`cat-${name}`} className="dsh-section" style={{ scrollMarginTop: 12 }}>
                  <SectionHeader icon={getCategoryIconComponent(name)} title={name} meta={`${items.length} moduler`} />
                  <div className="dsh-grid-apps">
                    {items.map(({ id, title, description }) => {
                      const badgeCount = id === 'varslinger' ? unreadNotificationsCount : id === 'foresporsler' ? assignedInquiriesCount : 0;
                      return (
                        <AppCard
                          key={id}
                          icon={getWidgetIcon(id)}
                          title={title}
                          description={description}
                          tone={getModuleTone(id)}
                          badge={badgeCount}
                          onClick={() => handleWidgetClick(id)}
                          disabled={isPreviewing}
                        />
                      );
                    })}
                  </div>
                </section>
              ))}
            </div>

            <section className="dsh-card" style={{ overflow: 'hidden', position: 'sticky', top: 0 }}>
              <div style={{ padding: '12px 14px 4px' }}>
                <SectionHeader
                  icon={FiList}
                  title="Dagens ordre"
                  meta={psum ? `${psum.todaysOrders.length} vist` : null}
                  right={<button type="button" className="dsh-link" onClick={() => openOrdersFlowWithFilter('mine-active')}>Se alle →</button>}
                />
              </div>
              {!psum ? (
                <p className="dsh-empty" style={{ padding: '0 14px 14px' }}>Laster…</p>
              ) : psum.todaysOrders.length === 0 ? (
                <p className="dsh-empty" style={{ padding: '0 14px 14px' }}>Ingen aktive ordre for dine team ✓</p>
              ) : (
                <div className="dsh-orders">
                  <OrderListHeader />
                  {psum.todaysOrders.map((o) => (
                    <OrderRow
                      key={o.id}
                      orderNumber={o.orderNumber}
                      title={o.customer || o.description}
                      meta={[o.description && o.customer ? o.description : null, o.deliveryDate ? `Frist ${o.deliveryDate}` : null, `${o.teamsDone}/${o.teamsTotal} team ferdig`].filter(Boolean).join(' · ')}
                      quantity={o.quantity}
                      status={o.status}
                      progress={o.progress}
                      onClick={() => openOrder(o.id)}
                    />
                  ))}
                </div>
              )}
            </section>
          </div>

          {/* ===== Siste ordre (recently added) ===== */}
          <section className="dsh-section">
            <SectionHeader
              icon={FiClock}
              title="Siste ordre"
              meta={psum?.recentOrders ? `${psum.recentOrders.length} nyeste` : null}
              right={<button type="button" className="dsh-link" onClick={() => openOrdersFlowWithFilter('all')}>Se alle →</button>}
            />
            {!psum ? (
              <p className="dsh-empty">Laster…</p>
            ) : !psum.recentOrders || psum.recentOrders.length === 0 ? (
              <p className="dsh-empty">Ingen ordre funnet</p>
            ) : (
              <div className="dsh-orders">
                <OrderListHeader />
                {psum.recentOrders.map((o) => (
                  <OrderRow
                    key={o.id}
                    orderNumber={o.orderNumber}
                    title={o.customer || o.description}
                    meta={[o.description && o.customer ? o.description : null, o.createdAt ? `Opprettet ${o.createdAt}` : null, o.deliveryDate ? `Frist ${o.deliveryDate}` : null].filter(Boolean).join(' · ')}
                    status={o.status}
                    onClick={() => openOrder(o.id)}
                  />
                ))}
              </div>
            )}
          </section>

          {/* ===== Hurtiglenker ===== */}
          {visibleLinks.length > 0 && (
            <section className="dsh-section">
              <SectionHeader icon={FiLink} title="Hurtiglenker" meta={`${visibleLinks.length}`} />
              <div className="dsh-grid-apps">
                {visibleLinks.map((link) => (
                  <AppCard
                    key={`link-${link.id}`}
                    emoji={link.icon || '🔗'}
                    title={link.title}
                    description={link.description || link.url}
                    tone="teal"
                    onClick={() => window.open(link.url, '_blank', 'noopener,noreferrer')}
                  />
                ))}
              </div>
            </section>
          )}
        </main>

        {/* ===== RIGHT SIDEBAR (operational panel) ===== */}
        <aside
          className={`dsh-aside ${rightPanelCollapsed ? 'is-collapsed' : ''}`}
          style={rightPanelBgUrl ? { backgroundImage: `linear-gradient(rgba(15,23,42,0.88), rgba(15,23,42,0.88)), url(${getServerUrl()}${rightPanelBgUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' } : undefined}
        >
          <div className="dsh-aside__top">
            <button type="button" onClick={() => setRightPanelCollapsed(!rightPanelCollapsed)} className="dsh-iconbtn" style={{ height: 30, minWidth: 30 }} title={rightPanelCollapsed ? 'Vis panel' : 'Skjul panel'}>
              <FiChevronLeft size={16} style={{ transform: rightPanelCollapsed ? 'none' : 'rotate(180deg)', transition: 'transform 0.2s' }} />
            </button>
            {!rightPanelCollapsed && <span className="dsh-aside__title" style={rightPanelTextColor ? { color: rightPanelTextColor } : undefined}>Driftspanel</span>}
          </div>

          {rightPanelCollapsed ? (
            <div className="dsh-aside__rail">
              <button type="button" onClick={() => handleWidgetClick('varslinger')} className="dsh-iconbtn" title="Varsler">
                <FiBell size={17} />
                {(unreadNotificationsCount > 0 || stats?.stoppedOrders > 0) && <span className="dsh-badge-dot" style={{ minWidth: 9, height: 9, padding: 0, top: 2, right: 2 }} />}
              </button>
              {quickActions.map((a, i) => (
                <button key={i} type="button" onClick={() => handleWidgetClick(a.widgetId)} className="dsh-iconbtn" title={a.label}>{React.createElement(a.icon, { size: 17 })}</button>
              ))}
            </div>
          ) : (
            <div className="dsh-aside__scroll">
              {/* Varsler */}
              <div className="dsh-aside__section">
                <div className="dsh-aside__head">
                  <h3 className="dsh-aside__h" style={rightPanelTextColor ? { color: rightPanelTextColor } : undefined}>Varsler</h3>
                  <button type="button" className="dsh-link" onClick={() => handleWidgetClick('varslinger')}>Se alle →</button>
                </div>
                {stats?.stoppedOrders > 0 && (
                  <>
                    <AlertItem icon={FiAlertTriangle} severity="danger" message={`${stats.stoppedOrders} ordre stoppet`} sub="Krever oppmerksomhet" expandable open={expandedAlert === 'stopped'} onClick={() => handleAlertToggle('stopped')} />
                    {renderAlertList('stopped')}
                  </>
                )}
                {stats?.overdueOrders > 0 && (
                  <>
                    <AlertItem icon={FiClock} severity="warning" message={`${stats.overdueOrders} ordre forsinket`} sub="Passert leveringsfrist" expandable open={expandedAlert === 'overdue'} onClick={() => handleAlertToggle('overdue')} />
                    {renderAlertList('overdue')}
                  </>
                )}
                {recentNotifications.map((n) => (
                  <AlertItem
                    key={n.id}
                    icon={FiBell}
                    severity="info"
                    message={n.message}
                    sub={[n.orderNumber, n.customer].filter(Boolean).join(' · ') || null}
                    time={fmtTime(n.createdAt || n.created_at)}
                    onClick={() => handleWidgetClick('varslinger')}
                  />
                ))}
                {unreadNotificationsCount > recentNotifications.length && (
                  <button type="button" className="dsh-link" style={{ fontSize: 11.5 }} onClick={() => handleWidgetClick('varslinger')}>+{unreadNotificationsCount - recentNotifications.length} flere uleste varsler</button>
                )}
                {(!stats?.stoppedOrders && !stats?.overdueOrders && recentNotifications.length === 0) && (
                  <p className="dsh-empty" style={{ padding: '4px 0' }}>Ingen varsler akkurat nå ✓</p>
                )}
              </div>

              {/* Hurtigfiltre */}
              {widgets.some((w) => w.id === 'ordersFlow') && (
                <div className="dsh-aside__section">
                  <div className="dsh-aside__head">
                    <h3 className="dsh-aside__h" style={rightPanelTextColor ? { color: rightPanelTextColor } : undefined}>Hurtigfiltre</h3>
                    <span style={{ fontSize: 10.5, color: 'var(--dsh-text-faint)' }}>Åpner Ordreflyt</span>
                  </div>
                  <div className="dsh-filters">
                    <QuickFilter label="I produksjon" tone="primary" count={psum?.inProgress} onClick={() => openOrdersFlowWithFilter('pågår')} />
                    <QuickFilter label="Planlagt" tone="blue" count={psum?.planned} onClick={() => openOrdersFlowWithFilter('ny')} />
                    <QuickFilter label="Stoppet" tone="danger" count={stats?.stoppedOrders} onClick={() => handleAlertToggle('stopped')} title="Viser liste over stoppede ordre under Varsler" />
                    <QuickFilter label="Ferdig" tone="success" onClick={() => openOrdersFlowWithFilter('fullført')} />
                    <QuickFilter label="Klar til fakturering" tone="secondary" onClick={() => openOrdersFlowWithFilter('ready-for-invoicing')} />
                    <QuickFilter label="Alle ordre" tone="muted" onClick={() => openOrdersFlowWithFilter('all')} />
                  </div>
                </div>
              )}

              {/* Snarveier (customisable quick actions) */}
              <div className="dsh-aside__section">
                <div className="dsh-aside__head">
                  <h3 className="dsh-aside__h" style={rightPanelTextColor ? { color: rightPanelTextColor } : undefined}>Snarveier</h3>
                  {!editingQuickActions && (
                    <button type="button" onClick={handleStartEditingQuickActions} className="dsh-iconbtn" style={{ height: 24, minWidth: 24, padding: 0 }} title="Tilpass snarveier"><FiSettings size={13} /></button>
                  )}
                </div>
                {editingQuickActions ? (
                  <div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 10 }}>
                      {editingActionIds.map((wid, idx) => {
                        const widget = availableWidgetsForActions.find((w) => w.id === wid);
                        if (!widget) return null;
                        return (
                          <div key={wid} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 8px', borderRadius: 8, background: 'var(--dsh-surface)' , border: '1px solid var(--dsh-border)' }}>
                            <span className="dsh-shortcut__icon" style={{ width: 20, height: 20 }}>{React.createElement(widget.icon, { size: 12 })}</span>
                            <span style={{ flex: 1, fontSize: 12, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{widget.title}</span>
                            <button type="button" onClick={() => moveActionUp(idx)} disabled={idx === 0} className="dsh-iconbtn" style={{ height: 22, minWidth: 22, padding: 0 }}><FiArrowUp size={11} /></button>
                            <button type="button" onClick={() => moveActionDown(idx)} disabled={idx === editingActionIds.length - 1} className="dsh-iconbtn" style={{ height: 22, minWidth: 22, padding: 0 }}><FiArrowDown size={11} /></button>
                            <button type="button" onClick={() => toggleActionSelection(wid)} className="dsh-iconbtn" style={{ height: 22, minWidth: 22, padding: 0, color: '#f87171' }}><FiX size={12} /></button>
                          </div>
                        );
                      })}
                      {editingActionIds.length === 0 && <p className="dsh-empty" style={{ padding: '4px 0' }}>Ingen valgte snarveier</p>}
                    </div>
                    {availableWidgetsForActions.filter((w) => !editingActionIds.includes(w.id)).length > 0 && (
                      <div style={{ marginBottom: 10 }}>
                        <p style={{ margin: '0 0 6px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: 'var(--dsh-text-faint)' }}>Legg til</p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: 170, overflowY: 'auto' }}>
                          {availableWidgetsForActions.filter((w) => !editingActionIds.includes(w.id)).map((w) => (
                            <button key={w.id} type="button" onClick={() => toggleActionSelection(w.id)} className="dsh-shortcut" style={{ height: 30 }}>
                              <span style={{ color: 'var(--dsh-text-faint)', width: 24, display: 'grid', placeItems: 'center' }}>{React.createElement(w.icon, { size: 13 })}</span>
                              <span style={{ flex: 1, fontSize: 12, color: 'var(--dsh-text-dim)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{w.title}</span>
                              <FiPlus size={12} style={{ color: '#4ade80' }} />
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <button type="button" onClick={handleSaveQuickActions} disabled={savingActions} className="dsh-btn dsh-btn--primary dsh-btn--xs" style={{ background: 'var(--dsh-success)', borderColor: 'var(--dsh-success)' }}><FiCheck size={12} /> Lagre</button>
                      <button type="button" onClick={() => setEditingActionIds([...defaultQuickActionIds])} className="dsh-btn dsh-btn--ghost dsh-btn--xs">Tilbakestill</button>
                      <button type="button" onClick={() => setEditingQuickActions(false)} className="dsh-btn dsh-btn--ghost dsh-btn--xs">Avbryt</button>
                    </div>
                  </div>
                ) : quickActions.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {quickActions.map((a, i) => (
                      <button key={i} type="button" className="dsh-shortcut" onClick={() => handleWidgetClick(a.widgetId)} style={rightPanelTextColor ? { color: rightPanelTextColor } : undefined}>
                        <span className="dsh-shortcut__icon">{React.createElement(a.icon, { size: 13 })}</span>
                        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.label}</span>
                        <FiChevronRight size={13} style={{ color: 'var(--dsh-text-faint)' }} />
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="dsh-empty" style={{ padding: '4px 0' }}>Ingen snarveier valgt. Klikk ⚙ for å legge til.</p>
                )}
              </div>

              {/* Fravær */}
              <div className="dsh-aside__section">
                <div className="dsh-aside__head">
                  <h3 className="dsh-aside__h" style={rightPanelTextColor ? { color: rightPanelTextColor } : undefined}>
                    Fravær {absenceDateLabel} <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, color: 'var(--dsh-text-faint)' }}>({absencesToday.length})</span>
                  </h3>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <button type="button" onClick={() => shiftAbsenceDate(-1)} className="dsh-iconbtn" style={{ height: 22, minWidth: 22, padding: 0 }} title="Forrige dag"><FiChevronLeft size={13} /></button>
                    {absenceDate !== todayIso && <button type="button" onClick={() => setAbsenceDate(todayIso)} className="dsh-link" style={{ fontSize: 10.5 }} title="Gå til i dag">i dag</button>}
                    <button type="button" onClick={() => shiftAbsenceDate(1)} className="dsh-iconbtn" style={{ height: 22, minWidth: 22, padding: 0 }} title="Neste dag"><FiChevronRight size={13} /></button>
                  </div>
                </div>
                {absencesToday.length > 0 ? (
                  <div>
                    {absencesToday.map((a, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 0', borderBottom: i < absencesToday.length - 1 ? '1px solid var(--dsh-border)' : 'none' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 9, minWidth: 0 }}>
                          <span className="dsh-avatar" style={{ width: 26, height: 26, fontSize: 10, background: 'var(--dsh-surface-3)' }}>{(a.name || '').split(' ').map((n) => n.charAt(0)).slice(0, 2).join('').toUpperCase()}</span>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 12.5, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.name}</div>
                            <div style={{ fontSize: 11, color: 'var(--dsh-text-faint)' }}>{a.typeLabel}</div>
                          </div>
                        </div>
                        {a.daysLeft != null && a.daysLeft > 0 && (
                          <StatusBadge tone="success" dot={false} title={a.endDate ? `Tilbake ${(() => { const d = new Date(`${a.endDate}T00:00:00`); do { d.setDate(d.getDate() + 1); } while (d.getDay() === 0 || d.getDay() === 6); return d.toLocaleDateString('nb-NO'); })()}` : undefined}>
                            {a.daysLeft}d
                          </StatusBadge>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="dsh-empty" style={{ padding: '4px 0' }}>Ingen registrert fravær</p>
                )}
              </div>
            </div>
          )}
        </aside>
      </div>

      {/* Felles Kalender fullscreen overlay */}
      {showFellesKalender && ReactDOM.createPortal(
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 2000, backgroundColor: '#0f172a', display: 'flex', flexDirection: 'column' }}>
          <div style={{ flex: 1, overflow: 'hidden' }}>
            <AlleAnsatteWidget
              token={token}
              currentUser={{
                ...user,
                isTeamLeader: userPermissions?.isTeamLeader,
                canCreateWorkOrder: userPermissions?.canCreateWorkOrder,
                canPlanAllTeams: userPermissions?.canPlanAllTeams,
                canPlanProduction: userPermissions?.canPlanProduction,
                canReserveDates: userPermissions?.canReserveDates,
                isVacationApprover: userPermissions?.isVacationApprover,
              }}
              onClose={() => setShowFellesKalender(false)}
              onOpenInOrdersFlow={(workOrderId) => {
                sessionStorage.setItem('selectedWorkOrderId', workOrderId);
                setShowFellesKalender(false);
                onWidgetSelect?.('ordersFlow');
              }}
            />
          </div>
        </div>,
        document.body
      )}

      {/* Unimicro Ordrer fullscreen overlay */}
      {showUnimicroOrders && ReactDOM.createPortal(
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 2000, backgroundColor: '#0f172a', display: 'flex', flexDirection: 'column' }}>
          <div style={{ flex: 1, overflow: 'hidden' }}>
            <UnimicroOrdersWidget token={token} onClose={() => setShowUnimicroOrders(false)} />
          </div>
        </div>,
        document.body
      )}

      {showRadialDial && (
        <RadialSpeedDial categories={radialCategories} onClose={() => setShowRadialDial(false)} onWidgetSelect={handleWidgetClick} />
      )}
      {showSpaceInvaders && <SpaceInvaders onClose={() => setShowSpaceInvaders(false)} />}
    </div>
  );
};

// Greeting based on time of day
function getGreeting() {
  const h = new Date().getHours();
  if (h < 6) return 'God natt';
  if (h < 10) return 'God morgen';
  if (h < 17) return 'Hei';
  if (h < 22) return 'God kveld';
  return 'God natt';
}

// ---- Module → semantic tone (used by AppCard icon containers) ----
const getModuleTone = (id) => {
  const ordreIds = ['foresporsler','workOrders','workOrdersList','ordersFlow','productionPlan','jobQueue','deliveryDisplay','deliveryTracking','timeline','bom','unimicrLookup','partsExplorer','incomingDelivery','dailyReport'];
  const teamIds = ['teamMembers','teamCapacity','employeeCalendar','anstattkalendar','teams','alleAnsatte','operatorWork','phoneDirectory','absenceRequest','brukere'];
  const maskinIds = ['machines','machineMonitor','alarmLog','machiningTools','cncProgramTransfer'];
  const lagerIds = ['steelCertificates','steelInventory','storageLocations','steelPurchases','forbruksskap','steelMeasure'];
  const komIds = ['varslinger','fileShare','feed','aiChat','email'];
  const verktoyIds = ['weather','vektKalkulator','pdfRedact','cadViewer','adamCad','smartScheduler','kpiDashboard','salesProgress','statistics'];
  if (ordreIds.includes(id)) return 'primary';
  if (teamIds.includes(id)) return 'success';
  if (maskinIds.includes(id)) return 'warning';
  if (lagerIds.includes(id)) return 'amber';
  if (komIds.includes(id)) return 'secondary';
  if (verktoyIds.includes(id)) return 'teal';
  return 'danger';
};

// ---- Category name → icon component (left nav + section headers) ----
const getCategoryIconComponent = (name) => {
  const map = {
    'Ordre & Produksjon': FiFileText, 'Team & Ansatte': FiUsers, 'Maskiner': FiTool,
    'Lager & Sertifikater': FiArchive, 'Kommunikasjon': FiBell, 'Verktøy': FiHash,
    'Administrasjon': FiSettings, 'Annet': FiGrid,
  };
  return map[name] || FiGrid;
};

// ---- Module preview cards (visual only, decorative motifs — no real data) ----
const getModuleAccent = (id) => {
  const ordreIds = ['foresporsler','workOrders','workOrdersList','ordersFlow','productionPlan','jobQueue','deliveryDisplay','deliveryTracking','timeline','bom','unimicrLookup','partsExplorer','incomingDelivery'];
  const teamIds = ['teamMembers','teamCapacity','employeeCalendar','anstattkalendar','teams','alleAnsatte','operatorWork','phoneDirectory','absenceRequest'];
  const maskinIds = ['machines','machineMonitor','alarmLog','cncProgramTransfer'];
  const lagerIds = ['steelCertificates','steelInventory','storageLocations','steelPurchases','machiningTools'];
  const komIds = ['varslinger','feed','aiChat','email'];
  const verktoyIds = ['weather','vektKalkulator','pdfRedact','cadViewer','adamCad','smartScheduler','kpiDashboard','salesProgress','statistics'];
  if (ordreIds.includes(id)) return '#3E6FA3';
  if (teamIds.includes(id)) return '#1E8A5F';
  if (maskinIds.includes(id)) return '#E8622C';
  if (lagerIds.includes(id)) return '#B7791F';
  if (komIds.includes(id)) return '#E8622C';
  if (verktoyIds.includes(id)) return '#3E6FA3';
  return '#C63D3D';
};

const getPreviewKind = (id) => {
  const map = {
    foresporsler:'list', jobQueue:'list', deliveryDisplay:'list', incomingDelivery:'list', phoneDirectory:'list', feed:'list', email:'list', varslinger:'list', auditLog:'list',
    workOrdersList:'bars', teamCapacity:'bars', operatorWork:'bars',
    bom:'table', steelCertificates:'table', steelInventory:'table', steelPurchases:'table', machiningTools:'table', unimicro:'table', statusConfig:'table',
    productionPlan:'gantt', timeline:'gantt',
    employeeCalendar:'calendar', anstattkalendar:'calendar', alleAnsatte:'calendar', absenceRequest:'calendar',
    machineMonitor:'chart', smartScheduler:'chart', kpiDashboard:'chart', salesProgress:'chart', statistics:'chart',
    unimicrLookup:'search', partsExplorer:'search',
    teamMembers:'avatars', teams:'avatars',
    machines:'grid', storageLocations:'grid', ordersFlow:'grid',
    aiChat:'chat',
  };
  return map[id] || 'cta';
};

const ModulePreview = ({ kind, accent }) => {
  const soft = `${accent}22`;
  switch (kind) {
    case 'list':
      return (
        <div className="tw-space-y-1.5 tw-w-full">
          {[0,1,2].map(i => (
            <div key={i} className="tw-flex tw-items-center tw-gap-2">
              <span className="tw-w-1.5 tw-h-1.5 tw-rounded-full tw-flex-shrink-0" style={{ background: accent }} />
              <span className="tw-h-1.5 tw-rounded-full tw-flex-1" style={{ background: '#e2e8f0' }} />
              <span className="tw-h-1.5 tw-w-5 tw-rounded-full" style={{ background: soft }} />
            </div>
          ))}
        </div>
      );
    case 'bars':
      return (
        <div className="tw-space-y-2 tw-w-full">
          {[75,45,60].map((w,i) => (
            <div key={i} className="tw-flex tw-items-center tw-gap-2">
              <span className="tw-h-1.5 tw-w-6 tw-rounded-full" style={{ background:'#e2e8f0' }} />
              <div className="tw-h-2 tw-flex-1 tw-rounded-full tw-overflow-hidden" style={{ background:'#eef2f6' }}>
                <div className="tw-h-full tw-rounded-full" style={{ width:`${w}%`, background: accent }} />
              </div>
            </div>
          ))}
        </div>
      );
    case 'table':
      return (
        <div className="tw-w-full tw-space-y-1">
          <div className="tw-grid tw-grid-cols-3 tw-gap-1">
            {[0,1,2].map(i => <span key={i} className="tw-h-2 tw-rounded" style={{ background: soft }} />)}
          </div>
          {[0,1,2].map(r => (
            <div key={r} className="tw-grid tw-grid-cols-3 tw-gap-1">
              {[0,1,2].map(c => <span key={c} className="tw-h-1.5 tw-rounded" style={{ background:'#e2e8f0' }} />)}
            </div>
          ))}
        </div>
      );
    case 'gantt':
      return (
        <div className="tw-space-y-2 tw-w-full">
          {[[10,50],[35,45],[20,60]].map(([ml,w],i) => (
            <div key={i} className="tw-flex tw-items-center tw-gap-2">
              <span className="tw-h-1.5 tw-w-5 tw-rounded-full" style={{ background:'#e2e8f0' }} />
              <div className="tw-relative tw-h-2 tw-flex-1">
                <div className="tw-absolute tw-h-full tw-rounded-full" style={{ left:`${ml}%`, width:`${w}%`, background: accent }} />
              </div>
            </div>
          ))}
        </div>
      );
    case 'calendar':
      return (
        <div className="tw-grid tw-grid-cols-7 tw-gap-1 tw-w-full">
          {Array.from({length:14}).map((_,i) => (
            <span key={i} className="tw-rounded" style={{ height:10, background: [3,4,9].includes(i) ? accent : '#e9edf2' }} />
          ))}
        </div>
      );
    case 'chart':
      return (
        <div className="tw-flex tw-items-end tw-gap-1.5 tw-w-full tw-h-full">
          {[40,70,50,90,60,80].map((h,i) => (
            <div key={i} className="tw-flex-1 tw-rounded-t" style={{ height:`${h}%`, background: i%2 ? soft : accent }} />
          ))}
        </div>
      );
    case 'search':
      return (
        <div className="tw-w-full tw-space-y-2">
          <div className="tw-flex tw-items-center tw-gap-1.5 tw-px-2 tw-py-1 tw-rounded-full" style={{ background:'#eef2f6' }}>
            <FiSearch size={9} style={{ color: accent }} />
            <span className="tw-h-1.5 tw-flex-1 tw-rounded-full" style={{ background:'#dbe1e8' }} />
          </div>
          {[0,1].map(i => <span key={i} className="tw-block tw-h-1.5 tw-rounded-full" style={{ width: i ? '60%' : '80%', background:'#e2e8f0' }} />)}
        </div>
      );
    case 'avatars':
      return (
        <div className="tw-w-full tw-space-y-2">
          <div className="tw-flex tw-gap-1.5">
            {[0,1,2,3].map(i => <span key={i} className="tw-w-5 tw-h-5 tw-rounded-full" style={{ background: i%2 ? soft : accent, opacity:0.85 }} />)}
          </div>
          <span className="tw-block tw-h-1.5 tw-w-3/4 tw-rounded-full" style={{ background:'#e2e8f0' }} />
        </div>
      );
    case 'grid':
      return (
        <div className="tw-grid tw-grid-cols-3 tw-gap-1.5 tw-w-full">
          {Array.from({length:6}).map((_,i) => (
            <span key={i} className="tw-rounded-md" style={{ height:16, background: soft, border:`1px solid ${accent}33` }} />
          ))}
        </div>
      );
    case 'chat':
      return (
        <div className="tw-w-full tw-space-y-1.5">
          <span className="tw-block tw-h-3 tw-w-3/5 tw-rounded-lg tw-rounded-bl-none" style={{ background:'#e2e8f0' }} />
          <span className="tw-block tw-h-3 tw-w-2/5 tw-ml-auto tw-rounded-lg tw-rounded-br-none" style={{ background: accent }} />
        </div>
      );
    default: // cta
      return (
        <div className="tw-flex tw-items-center tw-justify-center tw-w-full tw-h-full">
          <div className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-lg" style={{ background: soft, color: accent }}>
            <FiPlus size={12} />
            <span className="tw-h-1.5 tw-w-8 tw-rounded-full" style={{ background: accent, opacity:0.5 }} />
          </div>
        </div>
      );
  }
};

// Helper function to get icons for widgets
const getWidgetIcon = (id) => {
  const icons = {
    // Ordre & Produksjon
    'foresporsler': MdInbox,
    'workOrders': MdAssignment,
    'workOrdersList': MdListAlt,
    'ordersFlow': MdSyncAlt,
    'productionPlan': MdCalendarMonth,
    'jobQueue': MdBarChart,
    'deliveryDisplay': MdLocalShipping,
    'timeline': MdTimeline,
    'bom': MdExtension,
    'unimicrLookup': MdSearch,
    'partsExplorer': MdBuild,
    'incomingDelivery': MdInboxArchive,

    // Team & Ansatte
    'teamMembers': MdGroups,
    'teamCapacity': MdTrending,
    'employeeCalendar': MdCalendarToday,
    'anstattkalendar': MdDateRange,
    'teams': MdBusiness,
    'alleAnsatte': MdPublic,
    'operatorWork': MdEngineering,
    'phoneDirectory': MdPhone,
    'absenceRequest': MdBeachAccess,

    // Maskiner
    'machines': MdPrecisionManufacturing,
    'machineMonitor': MdMonitor,
    'alarmLog': MdNotificationsActive,
    'cncProgramTransfer': MdIosShare,

    // Lager & Sertifikater
    'steelCertificates': MdVerified,
    'steelInventory': MdWarehouse,
    'storageLocations': MdPlace,
    'steelPurchases': MdShoppingCart,
    'machiningTools': MdBuild,

    // Kommunikasjon
    'varslinger': MdNotifications,
    'fileShare': MdIosShare,
    'feed': MdNewspaper,
    'aiChat': MdSmartToy,
    'email': MdEmail,

    // Verktøy
    'weather': MdWbSunny,
    'vektKalkulator': MdScale,
    'pdfRedact': MdEdit,
    'cadViewer': MdDesignServices,
    'adamCad': MdSmartToy,
    'smartScheduler': MdBolt,
    'kpiDashboard': MdDashboard,
    'salesProgress': MdAnalytics,
    'statistics': MdAnalytics,

    // Administrasjon
    'adminPanel': MdAdminPanelSettings,
    'configuration': MdSettings,
    'quickLinks': MdLink,
    'roles': MdSupervisorAccount,
    'auditLog': MdHistory,
    'unimicro': MdStorage,
    'systemBackup': MdBackup,
    'widgetManager': MdWidgets,

    'brukere': MdPerson,
  };
  return icons[id] || MdWidgets;
};

const getWidgetEmoji = (id) => {
  const emojis = {
    'foresporsler': '📨',
    'workOrders': '📋',
    'workOrdersList': '📄',
    'ordersFlow': '🔄',
    'productionPlan': '🗓️',
    'jobQueue': '📊',
    'deliveryDisplay': '🚚',
    'deliveryTracking': '📦',
    'timeline': '⏱️',
    'bom': '🧩',
    'unimicrLookup': '🔍',
    'partsExplorer': '⚙️',
    'incomingDelivery': '📥',
    'teamMembers': '👥',
    'teamCapacity': '📈',
    'employeeCalendar': '📅',
    'anstattkalendar': '🗓️',
    'teams': '🏢',
    'alleAnsatte': '🌐',
    'operatorWork': '🛠️',
    'phoneDirectory': '📞',
    'absenceRequest': '🏖️',
    'machines': '🏭',
    'machineMonitor': '🖥️',
    'alarmLog': '🚨',
    'cncProgramTransfer': '📤',
    'steelCertificates': '🏅',
    'steelInventory': '🗄️',
    'storageLocations': '📍',
    'steelPurchases': '🛒',
    'machiningTools': '🔧',
    'forbruksskap': '🗃️',
    'varslinger': '🔔',
    'fileShare': '📤',
    'feed': '📰',
    'aiChat': '🤖',
    'email': '✉️',
    'weather': '🌤️',
    'vektKalkulator': '⚖️',
    'pdfRedact': '✏️',
    'cadViewer': '📐',
    'adamCad': '🛠️',
    'smartScheduler': '⚡',
    'kpiDashboard': '🎯',
    'salesProgress': '📈',
    'statistics': '📉',
    'adminPanel': '🛡️',
    'configuration': '⚙️',
    'quickLinks': '🔗',
    'roles': '👑',
    'auditLog': '📜',
    'unimicro': '💾',
    'systemBackup': '💿',
    'widgetManager': '🎛️',
    'brukere': '👤',
    'dailyReport': '📝',
    'mobileApp': '📱',
    'statusConfig': '🔧',
  };
  return emojis[id] || '📦';
};

export default MainForm;
